import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useDeferredValue,
} from "react";
import MainLayout from "../components/mainLayout";
import { ProjetosApi } from "../service/projetosApi";
import { showError, showInfo } from "../utils/toast";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { PriorityBadge } from "../components/ui/PriorityBadge";
import ProjetoFluxoChamado from "../components/projetos/ProjetoFluxoChamado";
import { api } from "../api";
import "../sass/pages/projetos.scss";

/* ==================== Constantes ==================== */
const STATUS = {
  PENDENTE: "PENDENTE",
  EM_ANDAMENTO: "EM_ANDAMENTO",
  TESTE: "TESTE",
  REAJUSTE: "REAJUSTE",
  TESTADO_APROVADO: "TESTADO_APROVADO",
  TESTADO_REPROVADO: "TESTADO_REPROVADO",
  AGUARDANDO_RETORNO_CLIENTE: "AGUARDANDO_RETORNO_CLIENTE",
  FINALIZADO: "FINALIZADO",
  PAUSADO: "PAUSADO",
  CONCLUIDO: "CONCLUIDO",
  CANCELADO: "CANCELADO",
};


// aliases -> canônico
const STATUS_ALIASES = {
  BACKLOG: "PENDENTE",
  PENDENTE: "PENDENTE",
  EM_ANDAMENTO: "EM_ANDAMENTO",
  ANDAMENTO: "EM_ANDAMENTO",
  EM_PROGRESSO: "EM_ANDAMENTO",
  REVISAO: "TESTE",
  REVISÃO: "TESTE",
  PARA_TESTE: "TESTE",
  TESTE: "TESTE",
  REAJUSTE: "REAJUSTE",
  "DEVOLVIDO PARA REAJUSTE": "REAJUSTE",
  "DEVOLUÇÃO PARA REAJUSTE": "REAJUSTE",
  "DEVOLUCAO PARA REAJUSTE": "REAJUSTE",
  RETRABALHO: "REAJUSTE",
  TESTADO_APROVADO: "TESTADO_APROVADO",
  "TESTADO APROVADO": "TESTADO_APROVADO",
  "TESTADO - APROVADO": "TESTADO_APROVADO",
  OK_TESTE: "TESTADO_APROVADO",
  TESTADO_REPROVADO: "TESTADO_REPROVADO",
  "TESTADO REPROVADO": "TESTADO_REPROVADO",
  "TESTADO - REPROVADO": "TESTADO_REPROVADO",
  FALHA_TESTE: "TESTADO_REPROVADO",
  AGUARDANDO_RETORNO_CLIENTE: "AGUARDANDO_RETORNO_CLIENTE",
  AGUARDANDO_RETORNO: "AGUARDANDO_RETORNO_CLIENTE",
  "AGUARDANDO RETORNO CLIENTE": "AGUARDANDO_RETORNO_CLIENTE",
  "AGUARDANDO RETORNO DO CLIENTE": "AGUARDANDO_RETORNO_CLIENTE",
  PAUSADO: "PAUSADO",
  CONCLUIDO: "CONCLUIDO",
  FINALIZADO: "FINALIZADO",
  CANCELADO: "CANCELADO",
  CANCELADA: "CANCELADO",
};

const STATUS_LABEL = {
  PENDENTE: "PENDENTE",
  EM_ANDAMENTO: "EM ANDAMENTO",
  TESTE: "PARA TESTE",
  REAJUSTE: "REAJUSTE",
  TESTADO_APROVADO: "TESTADO (APROVADO)",
  TESTADO_REPROVADO: "TESTADO (REPROVADO)",
  AGUARDANDO_RETORNO_CLIENTE: "AGUARDANDO RETORNO DO CLIENTE",
  FINALIZADO: "FINALIZADO",
  PAUSADO: "PAUSADO",
  CONCLUIDO: "CONCLUÍDO",
  CANCELADO: "CANCELADO",
};

// chips rápidos (aba Abertos)
export const STATUS_FOR_FILTER = Object.freeze([
  STATUS.PENDENTE,
  STATUS.EM_ANDAMENTO,
  STATUS.TESTE,
  STATUS.REAJUSTE,
  STATUS.TESTADO_APROVADO,
  STATUS.AGUARDANDO_RETORNO_CLIENTE,
  STATUS.FINALIZADO,
  STATUS.PAUSADO,
]);

const TASK_STATUSES = [
  STATUS.PENDENTE,
  STATUS.EM_ANDAMENTO,
  STATUS.TESTE,
  STATUS.REAJUSTE,
  STATUS.PAUSADO,
  STATUS.CONCLUIDO,
  STATUS.CANCELADO,
];

function normalizeStatus(s) {
  if (!s) return null;
  const up = String(s).trim().toUpperCase();
  return STATUS_ALIASES[up] ?? (STATUS[up] ? up : null);
}
function toApiStatusParam(s) {
  const canon = normalizeStatus(s);
  return canon ? canon.toLowerCase() : undefined;
}
const PRIORIDADES = ["BAIXA", "MEDIA", "ALTA", "CRITICA"];

const TIPOS_PROJETO = [
  "PROJETO",
  "MELHORIA",
  "CORRECAO",
  "INCIDENTE",
  "URGENTE",
  "DEBITO_TECNICO",
];

const TIPO_PROJETO_LABEL = {
  PROJETO: "Projeto",
  MELHORIA: "Melhoria",
  CORRECAO: "Correção",
  INCIDENTE: "Incidente",
  URGENTE: "Urgente",
  DEBITO_TECNICO: "Débito técnico",
};

const AUDIT_EVENT_LABEL = {
  ATRIBUICAO: "Movimentação da equipe",
  PARTICIPANTE_ATUALIZADO: "Participante atualizado",
  TRABALHO_INICIADO: "Trabalho iniciado",
  TRABALHO_ENCERRADA: "Trabalho encerrado",
  TRABALHO_INTERROMPIDA: "Trabalho interrompido",
  TRABALHO_CANCELADA: "Sessão cancelada",
  INTERROMPIDO: "Atividade transferida",
  INCIDENTE_PROVISORIO_CRIADO: "Incidente provisório criado",
  BLOQUEIO_ABERTO: "Bloqueio registrado",
  BLOQUEIO_RESOLVIDO: "Bloqueio resolvido",
  EDIT: "Projeto atualizado",
  STATUS: "Status alterado",
  NOTE: "Registro do projeto",
  SESSAO: "Sessão de trabalho",
  INTERRUPCAO: "Interrupção",
  BLOQUEIO: "Bloqueio",
};

const AUDIT_ROLE_LABEL = {
  GESTOR: "Gestor",
  RESPONSAVEL_PRINCIPAL: "Responsável principal",
  DESENVOLVEDOR: "Desenvolvedor",
  COLABORADOR: "Colaborador",
  TESTADOR: "Testador",
  HOMOLOGADOR: "Homologador",
};

const AUDIT_INTERRUPTION_LABEL = {
  ATENDIMENTO_URGENTE: "Atendimento urgente",
  ALTERACAO_PRIORIDADE: "Alteração de prioridade",
  SOLICITACAO_GESTAO: "Solicitação da gestão",
  INCIDENTE: "Incidente",
  OUTRO: "Outro motivo",
};

const AUDIT_BLOCK_LABEL = {
  CLIENTE: "Dependência do cliente",
  TECNICO: "Bloqueio técnico",
  TERCEIRO: "Dependência de terceiro",
  AMBIENTE: "Problema de ambiente",
  REGRA_NEGOCIO: "Regra de negócio",
  OUTRO: "Outro bloqueio",
};

const AUDIT_EDIT_FIELD_LABEL = {
  TITULO: "Título",
  DESCRICAO: "Descrição",
  PRIORIDADE: "Prioridade",
  STATUS: "Status",
  PROGRESSO: "Progresso",
  DT_INICIO: "Data de início",
  DT_PREVISTA: "Previsão",
  DT_CONCLUSAO: "Data de conclusão",
  TIPO_PROJETO: "Tipo do projeto",
  CADASTRO_PROVISORIO: "Cadastro provisório",
  GESTOR_PROJETO_ID: "Gestor",
};

function auditEnumLabel(value, map, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;

  const normalized = String(value)
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s*[-/]\s*/g, "_")
    .replace(/\s+/g, "_");

  return (
    map?.[normalized] ||
    normalized
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase())
  );
}

function auditBooleanLabel(value) {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();

  if (["SIM", "TRUE", "1", "S"].includes(normalized)) return "Sim";
  if (["NAO", "NÃO", "FALSE", "0", "N"].includes(normalized)) return "Não";

  return value === true ? "Sim" : value === false ? "Não" : "-";
}

function auditEscapeRegExp(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function auditDescriptionBody(text) {
  return String(text || "")
    .replace(/^\[[^\]]+\]\s*/, "")
    .trim();
}

function auditExtractValue(text, key) {
  const body = auditDescriptionBody(text);
  const expression = new RegExp(
    `(?:^|;\\s*)${auditEscapeRegExp(key)}\\s*=\\s*([^;]+)`,
    "i"
  );
  const match = body.match(expression);

  if (!match) return null;

  return String(match[1] || "")
    .trim()
    .replace(/\.\s+.*$/, "")
    .replace(/\.$/, "")
    .trim();
}

function auditExtractAnyValue(text, keys = []) {
  for (const key of keys) {
    const value = auditExtractValue(text, key);
    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
  }

  return null;
}

function auditExtractPersonId(update, text, keys = []) {
  const directCandidates = [
    update?.ID_DEV,
    update?.DEV_ID,
    update?.ID_FUNCIONARIO,
    update?.PARTICIPANTE_ID,
    update?.RESPONSAVEL_ID,
    update?.RESPONSAVEL_RESOLUCAO_ID,
  ];

  for (const candidate of directCandidates) {
    const id = Number(candidate);
    if (id > 0) return id;
  }

  const extracted = Number(auditExtractAnyValue(text, keys));
  return extracted > 0 ? extracted : null;
}

function auditExtractTrailingNote(text) {
  const raw = String(text || "").trim();
  const closingField = raw.match(/(?:^|;\s*)[^=;]+\s*=\s*[^;]+?\.\s+(.+)$/);

  if (closingField?.[1]) {
    return closingField[1].trim();
  }

  return null;
}

function auditExtractCode(description) {
  const match = String(description || "").match(/^\[([^\]]+)\]/);
  return match?.[1]?.trim()?.toUpperCase() || null;
}

function auditExtractPersonIds(updates = []) {
  const ids = new Set();

  (updates || []).forEach((update) => {
    [
      update?.AUTOR_ID,
      update?.ID_DEV,
      update?.DEV_ID,
      update?.ID_FUNCIONARIO,
      update?.PARTICIPANTE_ID,
      update?.RESPONSAVEL_ID,
      update?.RESPONSAVEL_RESOLUCAO_ID,
      update?.CREATED_BY,
      update?.RESOLVIDO_BY,
    ].forEach((candidate) => {
      const id = Number(candidate);
      if (id > 0) ids.add(id);
    });

    const description = auditDescriptionBody(update?.DESCRICAO || "");
    const personKeys = [
      "devId",
      "dev",
      "idDev",
      "ID_DEV",
      "funcionarioId",
      "idFuncionario",
      "participanteId",
      "solicitanteId",
      "responsavelId",
      "responsavelResolucaoId",
      "resolvidoBy",
      "createdBy",
      "autorId",
    ];

    personKeys.forEach((key) => {
      const expression = new RegExp(
        `(?:^|[;\\s])${auditEscapeRegExp(key)}\\s*=\\s*(\\d+)`,
        "gi"
      );

      let match;
      while ((match = expression.exec(description)) !== null) {
        const id = Number(match[1]);
        if (id > 0) ids.add(id);
      }
    });
  });

  return Array.from(ids);
}

function auditCleanGenericText(description) {
  return String(description || "")
    .replace(/^\[[^\]]+\]\s*/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function buildAuditMovement(update, resolveName) {
  const rawDescription = String(update?.DESCRICAO || "").trim();
  const code = auditExtractCode(rawDescription);
  const baseType = String(update?.TIPO || "").trim().toUpperCase();
  const authorName = resolveName(update?.AUTOR_ID);
  const details = [];
  let title =
    AUDIT_EVENT_LABEL[code] ||
    AUDIT_EVENT_LABEL[baseType] ||
    auditEnumLabel(baseType, AUDIT_EVENT_LABEL, "Movimentação registrada");
  let summary = "";
  let note = null;
  let tone = "neutral";

  const addDetail = (label, value, detailTone = null) => {
    if (value === null || value === undefined || value === "" || value === "-") {
      return;
    }

    details.push({
      label,
      value: String(value),
      tone: detailTone,
    });
  };

  if (code === "ATRIBUICAO") {
    const devId = auditExtractPersonId(update, rawDescription, [
      "devId",
      "dev",
      "idDev",
      "ID_DEV",
      "participanteId",
      "idFuncionario",
    ]);
    const participantName = resolveName(devId);
    const exclusive = auditBooleanLabel(
      auditExtractValue(rawDescription, "exclusivo")
    );
    const principal = auditBooleanLabel(
      auditExtractValue(rawDescription, "principal")
    );
    const role = auditEnumLabel(
      auditExtractValue(rawDescription, "papel"),
      AUDIT_ROLE_LABEL,
      "Desenvolvedor"
    );

    const isExclusive = exclusive === "Sim";

    const participantIdentification =
      devId > 0 ? `Técnico #${devId}` : "Identificação indisponível";

    title = isExclusive
      ? "Responsável principal definido"
      : "Participante adicionado ao projeto";

    summary = isExclusive
      ? `${participantName} foi definido como responsável principal do projeto e substituiu a equipe anterior.`
      : `${participantName} foi adicionado ao projeto e passou a integrar a equipe como ${role.toLowerCase()}.`;

    addDetail(
      "Ação realizada",
      isExclusive
        ? "Responsável principal definido"
        : "Adicionado ao projeto",
      isExclusive ? "warning" : "success"
    );
    addDetail("Participante", participantName, "person");
    addDetail("Identificação", participantIdentification);
    addDetail("Papel no projeto", role, "role");
    addDetail(
      "Responsável principal",
      principal,
      principal === "Sim" ? "success" : null
    );
    addDetail(
      "Atribuição exclusiva",
      exclusive,
      isExclusive ? "warning" : "success"
    );
    addDetail(
      "Equipe anterior substituída",
      isExclusive ? "Sim" : "Não",
      isExclusive ? "warning" : "success"
    );

    note = auditExtractTrailingNote(rawDescription);
    tone = isExclusive ? "warning" : "team";
  } else if (code === "PARTICIPANTE_ATUALIZADO") {
    const devId = auditExtractPersonId(update, rawDescription, [
      "devId",
      "dev",
      "idDev",
      "ID_DEV",
      "participanteId",
      "idFuncionario",
    ]);
    const participantName = resolveName(devId);

    title = "Dados do participante atualizados";
    summary = `${participantName} teve papel, permissão ou responsabilidade revisados.`;

    addDetail("Participante", participantName, "person");
    addDetail(
      "Identificação",
      devId > 0 ? `Técnico #${devId}` : "Não disponível"
    );

    const remainder = auditCleanGenericText(rawDescription)
      .replace(/^devId\s*=\s*\d+\s*;?\s*/i, "")
      .trim();

    if (
      remainder &&
      remainder.toLowerCase() !== "sem justificativa"
    ) {
      note = remainder;
    }

    tone = "team";
  } else if (code === "TRABALHO_INICIADO") {
    const devId =
      auditExtractPersonId(update, rawDescription, [
        "devId",
        "dev",
        "idDev",
        "ID_DEV",
        "funcionarioId",
        "idFuncionario",
      ]) || Number(update?.AUTOR_ID) || null;
    const taskId = Number(
      auditExtractAnyValue(rawDescription, [
        "tarefa",
        "tarefaId",
        "idTarefa",
        "ID_TAREFA",
      ])
    );
    const developerName = resolveName(devId);

    title = "Trabalho iniciado";
    summary = `${developerName} iniciou uma sessão de trabalho no projeto.`;

    addDetail("Profissional", developerName, "person");
    addDetail(
      "Identificação",
      devId > 0 ? `Técnico #${devId}` : "Não disponível"
    );
    addDetail(
      "Atividade",
      taskId > 0 ? `Tarefa #${taskId}` : "Atividade geral do projeto"
    );

    tone = "success";
  } else if (
    code === "TRABALHO_ENCERRADA" ||
    code === "TRABALHO_INTERROMPIDA" ||
    code === "TRABALHO_CANCELADA"
  ) {
    const devId =
      auditExtractPersonId(update, rawDescription, [
        "devId",
        "dev",
        "idDev",
        "ID_DEV",
        "funcionarioId",
        "idFuncionario",
      ]) || Number(update?.AUTOR_ID) || null;
    const developerName = resolveName(devId);
    const reason = auditExtractAnyValue(rawDescription, [
      "motivo",
      "motivoEncerramento",
      "observacao",
    ]);

    title =
      code === "TRABALHO_ENCERRADA"
        ? "Sessão de trabalho encerrada"
        : code === "TRABALHO_INTERROMPIDA"
        ? "Sessão de trabalho interrompida"
        : "Sessão de trabalho cancelada";

    summary = `${developerName} encerrou a atividade registrada no projeto.`;

    addDetail("Profissional", developerName, "person");
    addDetail(
      "Motivo",
      reason ? auditEnumLabel(reason, AUDIT_INTERRUPTION_LABEL) : "Não informado"
    );

    tone = code === "TRABALHO_ENCERRADA" ? "success" : "warning";
  } else if (code === "INTERROMPIDO") {
    const destinationProject = Number(
      auditExtractAnyValue(rawDescription, [
        "destinoProjeto",
        "idProjetoDestino",
        "projetoDestino",
      ])
    );
    const destinationTask = Number(
      auditExtractAnyValue(rawDescription, [
        "destinoTarefa",
        "idTarefaDestino",
        "tarefaDestino",
      ])
    );
    const interruptionType = auditExtractAnyValue(rawDescription, [
      "tipo",
      "tipoInterrupcao",
    ]);
    const reason = auditExtractAnyValue(rawDescription, [
      "motivo",
      "observacao",
    ]);

    title = "Atividade interrompida e direcionada";
    summary = destinationProject
      ? `O trabalho foi redirecionado para o projeto #${destinationProject}.`
      : "O trabalho foi interrompido e redirecionado.";

    addDetail(
      "Projeto de destino",
      destinationProject > 0 ? `Projeto #${destinationProject}` : null
    );
    addDetail(
      "Tarefa de destino",
      destinationTask > 0 ? `Tarefa #${destinationTask}` : null
    );
    addDetail(
      "Tipo da interrupção",
      auditEnumLabel(interruptionType, AUDIT_INTERRUPTION_LABEL)
    );
    addDetail("Motivo", reason);

    tone = "warning";
  } else if (code === "INCIDENTE_PROVISORIO_CRIADO") {
    const devId =
      auditExtractPersonId(update, rawDescription, [
        "devId",
        "dev",
        "idDev",
        "ID_DEV",
        "funcionarioId",
        "idFuncionario",
      ]) || Number(update?.AUTOR_ID) || null;
    const requesterId = Number(
      auditExtractAnyValue(rawDescription, [
        "solicitanteId",
        "idSolicitante",
        "solicitante",
      ])
    );

    title = "Incidente provisório registrado";
    summary = `${resolveName(devId)} iniciou o atendimento emergencial.`;

    addDetail("Responsável inicial", resolveName(devId), "person");
    addDetail(
      "Solicitante",
      requesterId > 0 ? resolveName(requesterId) : "Não informado",
      "person"
    );

    tone = "warning";
  } else if (code === "BLOQUEIO_ABERTO") {
    const blockType = auditExtractValue(rawDescription, "tipo");
    const reason = auditExtractValue(rawDescription, "motivo");

    title = "Bloqueio registrado";
    summary = reason || "Uma impedimento foi registrado no projeto.";

    addDetail("Tipo", auditEnumLabel(blockType, AUDIT_BLOCK_LABEL));
    addDetail("Motivo", reason);

    tone = "danger";
  } else if (code === "BLOQUEIO_RESOLVIDO") {
    const solution = auditCleanGenericText(rawDescription);

    title = "Bloqueio resolvido";
    summary = "O impedimento foi encerrado e a solução ficou registrada.";
    addDetail("Solução", solution);

    tone = "success";
  } else if (code === "EDIT") {
    const changedMatch = rawDescription.match(/campos alterados:\s*(.+)$/i);
    const changedFields = String(changedMatch?.[1] || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((field) => auditEnumLabel(field, AUDIT_EDIT_FIELD_LABEL));

    title = "Informações do projeto atualizadas";
    summary = changedFields.length
      ? `${changedFields.length} campo(s) foram alterados.`
      : "Os dados principais do projeto foram atualizados.";

    changedFields.forEach((field) => addDetail("Campo alterado", field));
    tone = "edit";
  } else if (baseType === "STATUS" || update?.STATUS_NOVO) {
    const status = normalizeStatus(update?.STATUS_NOVO) || update?.STATUS_NOVO;
    const statusLabel = STATUS_LABEL[status] || auditEnumLabel(status);

    title = "Status do projeto alterado";
    summary = statusLabel
      ? `O projeto passou para ${String(statusLabel).toLowerCase()}.`
      : "O status do projeto foi atualizado.";

    addDetail("Novo status", statusLabel, "status");
    tone = "status";
  } else {
    const cleaned = auditCleanGenericText(rawDescription);

    title =
      AUDIT_EVENT_LABEL[baseType] ||
      auditEnumLabel(baseType, AUDIT_EVENT_LABEL, "Movimentação registrada");

    summary =
      cleaned ||
      (update?.STATUS_NOVO
        ? `Novo status: ${
            STATUS_LABEL[normalizeStatus(update.STATUS_NOVO)] ||
            auditEnumLabel(update.STATUS_NOVO)
          }`
        : "Movimentação registrada no histórico do projeto.");

    if (update?.ID_TAREFA) {
      addDetail("Tarefa", `#${update.ID_TAREFA}`);
    }

    if (update?.ID_BLOQUEIO) {
      addDetail("Bloqueio", `#${update.ID_BLOQUEIO}`);
    }

    if (update?.ID_SESSAO) {
      addDetail("Sessão", `#${update.ID_SESSAO}`);
    }
  }

  return {
    code,
    title,
    summary,
    note,
    details,
    tone,
    authorName,
  };
}

function AuditMovement({ update, resolveName }) {
  const movement = buildAuditMovement(update, resolveName);

  return (
    <article
      className={`project-timeline-item audit-movement audit-tone-${movement.tone}`}
    >
      <span className="timeline-dot audit-timeline-dot" />

      <div className="audit-movement-content">
        <div className="timeline-title-line audit-title-line">
          <div>
            <span className="audit-event-category">
              {AUDIT_EVENT_LABEL[String(update?.TIPO || "").toUpperCase()] ||
                "Histórico do projeto"}
            </span>
            <strong>{movement.title}</strong>
          </div>

          <time dateTime={update?.CREATED_AT || undefined}>
            {update?.CREATED_AT
              ? new Date(update.CREATED_AT).toLocaleString("pt-BR")
              : "-"}
          </time>
        </div>

        {movement.summary && (
          <p className="audit-summary">{movement.summary}</p>
        )}

        {movement.details.length > 0 && (
          <dl className="audit-details-grid">
            {movement.details.map((detail, index) => (
              <div
                key={`${detail.label}-${detail.value}-${index}`}
                className={`audit-detail ${
                  detail.tone ? `audit-detail-${detail.tone}` : ""
                }`}
              >
                <dt>{detail.label}</dt>
                <dd>{detail.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {movement.note && (
          <div className="audit-note">
            <span>Justificativa / orientação</span>
            <p>{movement.note}</p>
          </div>
        )}

        <footer className="audit-footer">
          <span className="audit-author-avatar" aria-hidden="true">
            {String(movement.authorName || "?")
              .trim()
              .charAt(0)
              .toUpperCase()}
          </span>

          <span>
            Registrado por <strong>{movement.authorName}</strong>
          </span>
        </footer>
      </div>
    </article>
  );
}

const PAPEIS_PROJETO = [
  "RESPONSAVEL_PRINCIPAL",
  "DESENVOLVEDOR",
  "COLABORADOR",
  "TESTADOR",
  "HOMOLOGADOR",
];

const PAPEL_PROJETO_LABEL = {
  RESPONSAVEL_PRINCIPAL: "Responsável principal",
  DESENVOLVEDOR: "Desenvolvedor",
  COLABORADOR: "Colaborador",
  TESTADOR: "Testador",
  HOMOLOGADOR: "Homologador",
};

const TIPOS_BLOQUEIO = [
  "AGUARDANDO_CLIENTE",
  "AGUARDANDO_ACESSO",
  "AGUARDANDO_DEFINICAO",
  "AGUARDANDO_DESENVOLVEDOR",
  "AGUARDANDO_TERCEIRO",
  "BLOQUEIO_TECNICO",
  "OUTRO",
];

const TIPO_BLOQUEIO_LABEL = {
  AGUARDANDO_CLIENTE: "Aguardando cliente",
  AGUARDANDO_ACESSO: "Aguardando acesso",
  AGUARDANDO_DEFINICAO: "Aguardando definição",
  AGUARDANDO_DESENVOLVEDOR: "Aguardando desenvolvedor",
  AGUARDANDO_TERCEIRO: "Aguardando terceiro",
  BLOQUEIO_TECNICO: "Bloqueio técnico",
  OUTRO: "Outro",
};

const isTrue = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value || "").toLowerCase() === "true";

const formatDuration = (secondsValue) => {
  const seconds = Math.max(0, Math.floor(Number(secondsValue) || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secondsRest = seconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(secondsRest).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(secondsRest).padStart(
    2,
    "0"
  )}`;
};

const getActiveParticipants = (project) =>
  (project?.atribuidos || []).filter(
    (item) =>
      item?.ATIVO === undefined ||
      item?.ATIVO === null ||
      isTrue(item?.ATIVO)
  );

const getPrincipalParticipant = (project) =>
  getActiveParticipants(project).find((item) =>
    isTrue(item?.RESPONSAVEL_PRINCIPAL)
  ) || getActiveParticipants(project)[0] || null;
// peso de criticidade (quanto maior, mais crítico)
const PRIORIDADE_WEIGHT = Object.freeze({
  BAIXA: 0,
  MEDIA: 1,
  ALTA: 2,
  CRITICA: 3,
});

const STATUS_BLOQUEADOS_PROJETO_CHAMADO = new Set([
  STATUS.FINALIZADO,
  STATUS.CONCLUIDO,
  STATUS.AGUARDANDO_RETORNO_CLIENTE,
]);

const isProjetoOriginadoDeChamado = (projeto) => {
  const id = projeto?.ID_CHAMADO_ORIGEM ?? projeto?.idChamadoOrigem;
  return id !== null && id !== undefined && String(id).trim() !== "";
};

const getStatusOptionsForProject = (projeto, statusAtual = null) => {
  const atual = normalizeStatus(statusAtual);
  const originado = isProjetoOriginadoDeChamado(projeto);

  return Object.values(STATUS).filter((item) => {
    if (!originado) return true;
    if (item === atual) return true;
    return !STATUS_BLOQUEADOS_PROJETO_CHAMADO.has(item);
  });
};

const getSessionValue = (...keys) => {
  for (const key of keys) {
    const value = sessionStorage.getItem(key);
    if (value !== null && String(value).trim()) return String(value).trim();
  }
  return "";
};

/* ==================== Helpers / Debug ==================== */
const DEBUG = (localStorage.getItem("DEBUG_PROJETOS") ?? "1") !== "0";
let __RID = 0;
const startLog = (scope, extra = {}) => {
  const id = ++__RID;
  const t0 = performance.now();
  if (DEBUG)
    console.groupCollapsed(
      `%c🔎 ${scope} #${id}`,
      "color:#2563eb;font-weight:700;",
      extra
    );
  const done = (label = "done") => {
    if (DEBUG) {
      console.log(`⏱️ ${label} in ${(performance.now() - t0).toFixed(1)}ms`);
      console.groupEnd();
    }
  };
  return { done };
};
const tryParse = (v) => {
  try {
    return typeof v === "string" ? JSON.parse(v) : v;
  } catch {
    return v;
  }
};
const axiosInfo = (e) => ({
  message: e?.message,
  status: e?.response?.status,
  data: e?.response?.data,
  url: e?.config?.url,
  method: e?.config?.method,
  params: e?.config?.params,
  payload: tryParse(e?.config?.data),
});
const logHttpBefore = (title, { url, method, params, payload }) => {
  if (!DEBUG) return;
  const upper = typeof method === "string" ? method.toUpperCase() : method;
  console.groupCollapsed(
    `%c🌐 ${title}`,
    "color:#047857;font-weight:700;",
    upper,
    url
  );
  if (params) console.log("params →", params);
  if (payload !== undefined) console.log("payload →", payload);
  console.groupEnd();
};
const logHttpAfter = (title, resp) => {
  if (!DEBUG) return;
  console.groupCollapsed(
    `%c✅ ${title}`,
    "color:#16a34a;font-weight:700;",
    resp?.status ?? 200,
    resp?.config?.url
  );
  console.log("response.data →", resp?.data);
  console.groupEnd();
};
const logHttpError = (title, e) => {
  if (!DEBUG) return;
  const info = axiosInfo(e);
  console.groupCollapsed(
    `%c⛔ ${title}`,
    "color:#dc2626;font-weight:700;",
    info.status,
    info.url
  );
  console.log("message →", info.message);
  console.log("data →", info.data);
  console.log("payload →", info.payload);
  console.log("params →", info.params);
  console.groupEnd();
};

function useDebounce(val, delay = 300) {
  const [v, setV] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setV(val), delay);
    return () => clearTimeout(t);
  }, [val, delay]);
  return v;
}

function toIsoWithSeconds(localDatetimeStr) {
  if (!localDatetimeStr) return undefined;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(localDatetimeStr))
    return `${localDatetimeStr}:00`;
  const d = new Date(localDatetimeStr);
  if (isNaN(d)) return undefined;
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours()
  )}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
}

/* ===== nomes (id -> nome) ===== */
const pickName = (obj) =>
  obj?.NOME ??
  obj?.Nome ??
  obj?.nome ??
  obj?.NOME_DEV ??
  obj?.DEV_NOME ??
  obj?.NM_DEV ??
  obj?.NOME_FUNCIONARIO ??
  obj?.FUNCIONARIO_NOME ??
  obj?.NM_FUNCIONARIO ??
  obj?.NOME_FUN ??
  obj?.NOME_COMPLETO ??
  obj?.NM_COMPLETO ??
  obj?.NOME_USUARIO ??
  obj?.NM_USUARIO ??
  obj?.USUARIO_NOME ??
  obj?.AUTOR_NOME ??
  obj?.CRIADOR_NOME ??
  obj?.CRIADO_POR ??
  obj?.GESTOR_NOME ??
  obj?.RESPONSAVEL_NOME ??
  obj?.NOME_RESPONSAVEL ??
  obj?.RESPONSAVEL_RESOLUCAO_NOME ??
  obj?.PARTICIPANTE_NOME ??
  obj?.NM_RAZAO_SOCIAL ??
  obj?.RAZAO_SOCIAL ??
  obj?.Usuario ??
  obj?.USUARIO ??
  null;

const normalizeBatchNames = (payload) => {
  if (!payload) return {};

  const root =
    payload?.data?.data ??
    payload?.data?.items ??
    payload?.data?.list ??
    payload?.data ??
    payload?.items ??
    payload?.list ??
    payload;

  if (root?.map && typeof root.map === "object" && !Array.isArray(root.map)) {
    const out = {};
    for (const [key, value] of Object.entries(root.map)) {
      const id = Number(key);
      const name =
        typeof value === "string"
          ? value
          : pickName(value) ?? value?.nome ?? value?.Nome;
      if (id > 0 && name) out[id] = String(name);
    }
    return out;
  }

  if (Array.isArray(root)) {
    const out = {};
    root.forEach((item) => {
      const id = Number(
        item?.IdFuncionario ??
          item?.ID_FUNCIONARIO ??
          item?.ID_DEV ??
          item?.ID ??
          item?.id
      );
      const name = pickName(item);
      if (id > 0 && name) out[id] = String(name);
    });
    return out;
  }

  if (typeof root === "object") {
    const directId = Number(
      root?.IdFuncionario ??
        root?.ID_FUNCIONARIO ??
        root?.ID_DEV ??
        root?.ID ??
        root?.id
    );
    const directName = pickName(root);

    if (directId > 0 && directName) {
      return { [directId]: String(directName) };
    }

    const out = {};
    for (const [key, value] of Object.entries(root)) {
      const id = Number(key);
      const name =
        typeof value === "string" ? value : pickName(value);
      if (id > 0 && name) out[id] = String(name);
    }
    return out;
  }

  return {};
};

/* ==================== UI ==================== */
const StatusBadge = ({ value }) => {
  const v = normalizeStatus(value) || "PENDENTE";
  const map = {
  PENDENTE: "badge-gray",
  EM_ANDAMENTO: "badge-amber",
  TESTE: "badge-blue",
  REAJUSTE: "badge-purple",
  TESTADO_APROVADO: "badge-emerald",
  TESTADO_REPROVADO: "badge-rose",
  AGUARDANDO_RETORNO_CLIENTE: "badge-gold",
  FINALIZADO: "badge-green",
  PAUSADO: "badge-slate",
  CONCLUIDO: "badge-green",
  CANCELADO: "badge-red",
};

  return (
    <span className={`badge ${map[v] || "badge-gray"}`}>{STATUS_LABEL[v]}</span>
  );
};

function PanelModal({
  open,
  title,
  onClose,
  children,
  size = "lg",
}) {
  const firstFieldRef = useRef(null);
  const onCloseRef = useRef(onClose);

  /*
   * Mantém a função de fechamento atualizada sem reiniciar o efeito do modal.
   * Funções declaradas dentro da tela ganham uma nova referência a cada
   * digitação; depender diretamente de onClose fazia o foco voltar ao primeiro
   * campo sempre que o formulário era atualizado.
   */
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const requestClose = useCallback(() => {
    onCloseRef.current?.();
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const onKey = (event) => {
      if (event.key === "Escape") requestClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);

    /*
     * O foco inicial é aplicado somente quando o modal abre. Alterações nos
     * campos não executam este efeito novamente.
     */
    const focusTimer = window.setTimeout(() => {
      const field = firstFieldRef.current;

      if (field && !field.disabled) {
        field.focus({ preventScroll: true });
      }
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, requestClose]);

  if (!open) return null;

  return (
    <div
      className="projects-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <section
        className={`projects-modal projects-modal-${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="projects-modal-header">
          <div>
            <span className="projects-modal-eyebrow">Gestão de projetos</span>
            <h2>{title}</h2>
          </div>

          <Button
            type="button"
            variant="ghost"
            onClick={requestClose}
            aria-label="Fechar modal"
          >
            Fechar
          </Button>
        </header>

        <div className="projects-modal-content">
          {typeof children === "function"
            ? children({ firstFieldRef })
            : children}
        </div>
      </section>
    </div>
  );
}

/* =============== helpers de sort local =============== */
const cmp = (a, b) => (a > b) - (a < b);
const safe = (v) => (v === null || v === undefined ? "" : v);
const getDate = (v) => (v ? new Date(v).getTime() : 0);
const sortRows = (rows, { key, dir }, accessors) => {
  if (!key) return rows;
  const acc = accessors[key];
  if (!acc) return rows;
  const mult = dir === "desc" ? -1 : 1;
  const copy = [...rows];
  copy.sort((r1, r2) => {
    const v1 = acc(r1);
    const v2 = acc(r2);
    if (typeof v1 === "number" && typeof v2 === "number")
      return mult * (v1 - v2);
    return (
      mult * cmp(String(safe(v1)).toUpperCase(), String(safe(v2)).toUpperCase())
    );
  });
  return copy;
};

// === DOWNLOAD HELPERS (mesmos da MinhaFilaDev) ===
function buildDownloadUrl(u, name = "") {
  if (!u) return "#";
  try {
    const url = new URL(u, window.location.origin);
    if (!url.searchParams.has("download")) {
      url.searchParams.set("download", name || "arquivo");
    }
    return url.toString();
  } catch {
    const sep = u.includes("?") ? "&" : "?";
    return `${u}${sep}download=${encodeURIComponent(name || "arquivo")}`;
  }
}

function saveBlob(blob, filename = "arquivo") {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "arquivo";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
}

/* ==================== Tela ==================== */
export default function ProjetosAdmin() {
  // modos / abas
  const [view, setView] = useState("lista"); // "lista" | "rank"
  const [tab, setTab] = useState("abertos"); // "abertos" | "concluidos" | "cancelados"
  const [detailTab, setDetailTab] = useState("visao");
  // filtros
  const [q, setQ] = useState("");
  const debouncedQ = useDebounce(q, 350);
  const deferredQ = useDeferredValue(debouncedQ);
  const [status, setStatus] = useState(""); // filtro extra de status
  const [tipoProjetoFiltro, setTipoProjetoFiltro] = useState("");
  const [situacaoEspecial, setSituacaoEspecial] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);

  // sort de servidor (quando aplicável) e sort local (todas as colunas)
  const [serverSort, setServerSort] = useState(() => {
    try {
      return localStorage.getItem("PROJETOS_SERVER_SORT") || "DT_PREVISTA:asc";
    } catch {
      return "DT_PREVISTA:asc";
    }
  });
  const [localSort, setLocalSort] = useState(() => {
    try {
      const raw = localStorage.getItem("PROJETOS_LOCAL_SORT");
      const parsed = raw ? JSON.parse(raw) : null;
      // saneia
      if (
        parsed &&
        typeof parsed.key === "string" &&
        (parsed.dir === "asc" || parsed.dir === "desc")
      ) {
        return parsed;
      }
    } catch {}
    return { key: "", dir: "asc" };
  }); // key nos headers

  // filtro: Setor → Funcionário
  const [filterCargo, setFilterCargo] = useState("");
  const [filterFuncs, setFilterFuncs] = useState([]);
  const [devId, setDevId] = useState("");
  const [loadingFilterFuncs, setLoadingFilterFuncs] = useState(false);

  // dados
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // criação
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formCreate, setFormCreate] = useState({
    codigo: "",
    titulo: "",
    descricao: "",
    prioridade: "MEDIA",
    tipoProjeto: "PROJETO",
    cadastroProvisorio: false,
    dtInicio: "",
    dtPrevista: "",
  });
  const [formCreateErrors, setFormCreateErrors] = useState({});
  const [createDebug, setCreateDebug] = useState(null);
  const [createCargo, setCreateCargo] = useState("");
  const [createFuncs, setCreateFuncs] = useState([]);
  const [createDevId, setCreateDevId] = useState("");
  const [loadingCreateFuncs, setLoadingCreateFuncs] = useState(false);
  const [createFiles, setCreateFiles] = useState([]);

  // edição
  const [editOpen, setEditOpen] = useState(false);
  const [editTab, setEditTab] = useState("form"); // "form" | "comentarios" | "anexos"
  const [editId, setEditId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formEdit, setFormEdit] = useState({
    titulo: "",
    descricao: "",
    prioridade: "MEDIA",
    tipoProjeto: "PROJETO",
    cadastroProvisorio: false,
    gestorProjetoId: "",
    dtInicio: "",
    dtPrevista: "",
  });
  const [formEditErrors, setFormEditErrors] = useState({});
  const [editDebug, setEditDebug] = useState(null);

  // atribuição + fila
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [assignDevId, setAssignDevId] = useState("");
  const [assignJust, setAssignJust] = useState("");
  const [assignStatus, setAssignStatus] = useState("");
  const [assignRole, setAssignRole] = useState("DESENVOLVEDOR");
  const [assignPrincipal, setAssignPrincipal] = useState(false);
  const [assignPodeMovimentar, setAssignPodeMovimentar] = useState(true);
  const [assignExclusive, setAssignExclusive] = useState(false);
  const [applying, setApplying] = useState(false);

  // ===== NOVO: Gerenciar fila (independente de atribuição) =====
  const [manageOpen, setManageOpen] = useState(false);
  const [manageDevId, setManageDevId] = useState(null);
  const [manageDevName, setManageDevName] = useState("-");
  const [manageQueueLoading, setManageQueueLoading] = useState(false);
  const [manageQueueItems, setManageQueueItems] = useState([]);
  const [manageQueueDirty, setManageQueueDirty] = useState(false);
  const [manageQueueFocusId, setManageQueueFocusId] = useState(null);
  const [manageQueueOriginalFocusId, setManageQueueOriginalFocusId] =
    useState(null);
  const [manageApplying, setManageApplying] = useState(false);

  // mapa projeto->dev
  const [assignedToMap, setAssignedToMap] = useState({});

  // catálogos
  const [cargos, setCargos] = useState([]);
  const [loadingCargos, setLoadingCargos] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState("");
  const [funcOptions, setFuncOptions] = useState([]);
  const [loadingFuncs, setLoadingFuncs] = useState(false);

  // detalhe
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!detail) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setDetail(null);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      window.removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [detail]);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [operationalBusy, setOperationalBusy] = useState(false);
  const [clockNow, setClockNow] = useState(Date.now());

  const [formTask, setFormTask] = useState({
    titulo: "",
    descricao: "",
    estimativaH: "",
    responsavelId: "",
    dtPrevista: "",
  });

  const [formBlock, setFormBlock] = useState({
    idTarefa: "",
    tipoBloqueio: "OUTRO",
    motivo: "",
    proximaAcao: "",
    responsavelResolucaoId: "",
    dtProximaRevisao: "",
  });

  const [resolucaoBloqueio, setResolucaoBloqueio] = useState({
    idBloqueio: null,
    texto: "",
  });

  // nomes
  const [peopleNames, setPeopleNames] = useState({});

  const getName = useCallback(
    (personOrId) => {
      const directName =
        personOrId && typeof personOrId === "object"
          ? pickName(personOrId)
          : null;

      if (directName) return String(directName);

      const id =
        personOrId && typeof personOrId === "object"
          ? Number(
              personOrId.ID_DEV ??
                personOrId.DEV_ID ??
                personOrId.PARTICIPANTE_ID ??
                personOrId.RESPONSAVEL_ID ??
                personOrId.RESPONSAVEL_RESOLUCAO_ID ??
                personOrId.AUTOR_ID ??
                personOrId.CREATED_BY ??
                personOrId.CRIADOR_ID ??
                personOrId.GESTOR_PROJETO_ID ??
                personOrId.IdFuncionario ??
                personOrId.ID_FUNCIONARIO ??
                personOrId.ID ??
                personOrId.id
            )
          : Number(personOrId);

      if (id > 0 && peopleNames[id]) {
        return peopleNames[id];
      }

      return id > 0 ? `Técnico #${id}` : "Não identificado";
    },
    [peopleNames],
  );
  const mergeNames = (obj) => {
    if (!obj) return;

    setPeopleNames((previous) => {
      const accepted = Object.entries(obj).filter(([id, name]) => {
        if (!id || !name) return false;

        const current = String(previous[id] || "").trim();
        return (
          !current ||
          /^#\d+$/.test(current) ||
          /^Técnico #\d+$/i.test(current) ||
          /^Não identificado$/i.test(current)
        );
      });

      if (!accepted.length) return previous;

      return {
        ...previous,
        ...Object.fromEntries(accepted),
      };
    });
  };

  useEffect(() => {
    if (!detail?.sessoesAtivas?.length) return undefined;

    const timer = window.setInterval(() => {
      setClockNow(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [detail?.sessoesAtivas?.length]);

  const getElapsedSessionSeconds = useCallback(
    (session) => {
      if (!session?.DT_INICIO) return Number(session?.DURACAO_SEGUNDOS) || 0;
      const start = new Date(session.DT_INICIO).getTime();
      if (!Number.isFinite(start)) return Number(session?.DURACAO_SEGUNDOS) || 0;

      const end = session.DT_FIM
        ? new Date(session.DT_FIM).getTime()
        : clockNow;

      return Math.max(0, Math.floor((end - start) / 1000));
    },
    [clockNow]
  );

  function EditableStatusCell({ project, id, status, onChange }) {
    const [editing, setEditing] = useState(false);
    const [localStatus, setLocalStatus] = useState(status);
    const [saving, setSaving] = useState(false);
    const originado = isProjetoOriginadoDeChamado(project);
    const options = getStatusOptionsForProject(project, localStatus);

    useEffect(() => {
      setLocalStatus(status);
    }, [status]);

    const handleChange = async (e) => {
      const newStatus = e.target.value;

      if (
        originado &&
        STATUS_BLOQUEADOS_PROJETO_CHAMADO.has(newStatus) &&
        newStatus !== normalizeStatus(status)
      ) {
        showError(
          "Projeto originado de chamado deve seguir até TESTADO E APROVADO e depois ser encaminhado ao suporte.",
        );
        setEditing(false);
        return;
      }

      setLocalStatus(newStatus);
      setSaving(true);
      try {
        await updateStatus(id, { status: newStatus });
        showInfo("Status atualizado.");
        onChange?.(newStatus);
      } catch (error) {
        showError(
          error?.response?.data?.message || "Falha ao atualizar status.",
        );
        setLocalStatus(status);
      } finally {
        setSaving(false);
        setEditing(false);
      }
    };

    return editing ? (
      <select
        value={localStatus}
        onChange={handleChange}
        onBlur={() => setEditing(false)}
        disabled={saving}
        className="input native project-status-select"
        title={
          originado
            ? "Projetos originados de chamados não podem ser finalizados ou concluídos manualmente."
            : "Alterar status"
        }
      >
        {options.map((item) => (
          <option
            key={item}
            value={item}
            disabled={
              originado &&
              STATUS_BLOQUEADOS_PROJETO_CHAMADO.has(item) &&
              item === normalizeStatus(localStatus)
            }
          >
            {STATUS_LABEL[item]}
          </option>
        ))}
      </select>
    ) : (
      <button
        type="button"
        className="project-status-trigger"
        onClick={() => setEditing(true)}
        title={
          originado
            ? "Fluxo protegido: clique para alterar apenas entre status permitidos."
            : "Clique para alterar o status."
        }
      >
        <StatusBadge value={localStatus} />
        {originado ? <span aria-hidden="true">🔒</span> : null}
      </button>
    );
  }

  // Baixar anexo respeitando auth/redirect
  async function downloadAttachmentProjetos(detail, anexo) {
    const name = anexo?.name || "arquivo";
    const primaryUrl = anexo?.downloadUrl || anexo?.url;
    if (!primaryUrl) return;

    // 1) tenta baixar direto (ex.: bucket público) com ?download=
    try {
      const publicUrl = buildDownloadUrl(primaryUrl, name);
      const res = await fetch(publicUrl, { mode: "cors", credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (blob.type && blob.type.includes("text/html")) {
        throw new Error("HTML error page");
      }
      saveBlob(blob, name);
      return;
    } catch (e) {
      // segue para fallback
    }

    // 2) backend gera link assinado OU streama o binário (ajuste as rotas se preciso)
    try {
      // a) preferencial: pedir um link assinado
      const { data } = await api.get(
        `/projetos/${detail.ID_PROJETO}/anexos/${anexo.id}/link`,
        { params: { filename: name } }
      );
      if (data?.url) {
        const signed = buildDownloadUrl(data.url, name);
        const res = await fetch(signed, { mode: "cors", credentials: "omit" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (blob.type && blob.type.includes("text/html")) {
          throw new Error("HTML error page (signed)");
        }
        saveBlob(blob, name);
        return;
      }
    } catch (e) {
      // b) fallback: pedir o arquivo em blob na própria API
      try {
        const resp = await api.get(
          `/projetos/${detail.ID_PROJETO}/anexos/${anexo.id}/download`,
          { responseType: "blob" }
        );
        saveBlob(resp.data, name);
        return;
      } catch {}
    }

    showError(
      "Não foi possível baixar o anexo. Verifique se o link é público ou tente novamente."
    );
  }

  function getLoggedUserId() {
    const idFromKey = Number(
      sessionStorage.getItem("IdFuncionario") ??
        localStorage.getItem("IdFuncionario")
    );
    if (idFromKey) return idFromKey;
    try {
      const raw =
        sessionStorage.getItem("user") ?? localStorage.getItem("user");
      if (raw) {
        const u = JSON.parse(raw);
        return (
          Number(u?.idFuncionario ?? u?.IdFuncionario ?? u?.id ?? u?.ID) || null
        );
      }
    } catch {}
    return null;
  }

  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [postingComment, setPostingComment] = useState(false);

  const getActiveProjectId = () => Number(detail?.ID_PROJETO ?? editId ?? 0);
  /* ===== Catálogos ===== */
  const normalizeCargos = (raw) => {
    if (!raw) return [];
    const arr = Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw)
      ? raw
      : [];
    const vals = arr
      .map((x) =>
        typeof x === "string"
          ? x.trim()
          : (
              x?.Cargo ||
              x?.Setor ||
              x?.Descricao ||
              x?.descricao ||
              x?.nome ||
              ""
            )
              .toString()
              .trim()
      )
      .filter(Boolean);
    return Array.from(new Set(vals));
  };
  const normalizeFuncs = (raw) => {
    const data = raw?.data || raw || [];
    const list = Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data)
      ? data
      : [];
    return list
      .map((f) => ({
        id: f.IdFuncionario ?? f.id ?? f.ID ?? null,
        label: f.Nome
          ? `${f.Nome}`
          : f.Usuario
          ? `${f.Usuario}`
          : `#${f.IdFuncionario ?? f.id ?? ""}`,
      }))
      .filter((o) => o.id);
  };
  const fetchCargos = async () => {
    if (cargos.length) return;
    setLoadingCargos(true);
    try {
      logHttpBefore("GET setores", { url: "/setores", method: "get" });
      const { data } = await api.get("/setores");
      logHttpAfter("GET setores", {
        data,
        status: 200,
        config: { url: "/setores" },
      });
      setCargos(normalizeCargos(data));
    } catch (e) {
      logHttpError("GET setores", e);
      showError("Falha ao carregar setores.");
    } finally {
      setLoadingCargos(false);
    }
  };
  const fetchFuncsByCargo = async (cargo, where = "assign") => {
    if (!cargo) {
      (where === "assign"
        ? setFuncOptions
        : where === "filter"
        ? setFilterFuncs
        : setCreateFuncs)([]);
      return;
    }
    const setLoading =
      where === "assign"
        ? setLoadingFuncs
        : where === "filter"
        ? setLoadingFilterFuncs
        : setLoadingCreateFuncs;
    const setOptions =
      where === "assign"
        ? setFuncOptions
        : where === "filter"
        ? setFilterFuncs
        : setCreateFuncs;
    setLoading(true);
    try {
      const url = `/func/setor/${encodeURIComponent(cargo)}`;
      logHttpBefore("GET funcs by setor", {
        url,
        method: "get",
        params: { situacao: "Ativo", bloqueado: 0 },
      });
      const { data } = await api.get(url, {
        params: { situacao: "Ativo", bloqueado: 0 },
      });
      logHttpAfter("GET funcs by setor", {
        data,
        status: 200,
        config: { url },
      });
      const arr = normalizeFuncs(data);
      setOptions(arr);

      /*
       * Os nomes recebidos para os selects também alimentam o cache global.
       * Assim auditoria, equipe, tarefas e sessões reutilizam a mesma
       * identificação sem precisar exibir apenas o ID.
       */
      mergeNames(
        Object.fromEntries(
          arr
            .filter((option) => option?.id && option?.label)
            .map((option) => [Number(option.id), String(option.label)])
        )
      );

      // auto-seleciona no CREATE se só existe 1 funcionário
      if (where === "create") {
        if (!createDevId && arr.length === 1) {
          setCreateDevId(String(arr[0].id));
        }
      }
    } catch (e) {
      logHttpError("GET funcs by setor", e);
      showError("Falha ao carregar funcionários do setor.");
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // nomes em lote
  const NAME_ENDPOINTS = ["/func/names"];
  const tryBatch = async (ids) => {
    for (const base of NAME_ENDPOINTS) {
      try {
        logHttpBefore("GET names (batch)", {
          url: base,
          method: "get",
          params: { ids: ids.join(",") },
        });
        const { data } = await api.get(base, {
          params: { ids: ids.join(",") },
        });
        logHttpAfter("GET names (batch)", {
          data,
          status: 200,
          config: { url: base },
        });
        const map = normalizeBatchNames(data);
        if (Object.keys(map).length) return map;
      } catch (e) {
        logHttpError("GET names (batch)", e);
      }
    }
    return {};
  };
  const tryOneByOne = async (ids) => {
    const out = {};
    await Promise.all(
      ids.map(async (id) => {
        try {
          const url = `/func/${id}`;
          logHttpBefore("GET func by id", { url, method: "get" });
          const { data } = await api.get(url);
          logHttpAfter("GET func by id", {
            data,
            status: 200,
            config: { url },
          });
          const candidate =
            data?.data?.data ??
            data?.data ??
            data?.funcionario ??
            data?.usuario ??
            data;
          const name = pickName(candidate);
          if (name) out[id] = String(name);
        } catch (e) {
          logHttpError("GET func by id", e);
        }
      })
    );
    return out;
  };
  const ensureNames = async (idsRaw) => {
    const ids = Array.from(
      new Set((idsRaw || []).map((x) => Number(x)).filter(Boolean))
    ).filter((id) => !peopleNames[id]);
    if (!ids.length) return;
    const lg = startLog("ensureNames", { ids });
    let map = await tryBatch(ids);
    if (!Object.keys(map).length) map = await tryOneByOne(ids);
    if (Object.keys(map).length) mergeNames(map);
    lg.done();
  };

  /* -------- Listagem / Rank -------- */
  const listAbortRef = useRef(null);
 // SUBSTITUA a função inteira por esta
const fetchList = async () => {
  listAbortRef.current?.abort?.();
  const controller = new AbortController();
  listAbortRef.current = controller;

  const lg = startLog("fetchList", { tab, status, q: deferredQ, devId, page, limit, serverSort });
  setLoading(true);

  try {
    const params = { page, limit };

    if (view === "lista" && tab === "abertos" && !status) params.openOnly = true;
    if (tab === "concluidos") params.status = toApiStatusParam(STATUS.CONCLUIDO);
    if (tab === "cancelados") params.status = toApiStatusParam(STATUS.CANCELADO);
    if (status) params.status = toApiStatusParam(status);
    if (devId) params.devId = Number(devId);

    // ✅ PRIMEIRO: parseia a busca
    const { id: idSearch, text: textSearch } = parseSearch(deferredQ);

    // ✅ Se for ID: ignora filtros e busca direto
    if (idSearch) {
      logHttpBefore("GET projeto (by idSearch)", { url: `/projetos/${idSearch}`, method: "get" });
      const resp = await api.get(`/projetos/${idSearch}`, { signal: controller.signal });
      logHttpAfter("GET projeto (by idSearch)", resp);

      const data = resp?.data;
      const normalized = data ? [{ ...data, STATUS: normalizeStatus(data.STATUS) || "PENDENTE" }] : [];
      setRows(normalized);
      setTotal(normalized.length);

      if (normalized.length) {
        const creatorIds = Array.from(new Set(normalized.map((r) => Number(r.CREATED_BY)).filter(Boolean)));
        if (creatorIds.length) ensureNames(creatorIds);
        buildAssignedToMap(normalized);
      }
      return;
    }

    // ✅ Se for texto: aplica no params.q
    if (textSearch) params.q = textSearch;

    if (/^[A-Za-z_]+:(asc|desc)$/i.test(serverSort)) params.sort = serverSort;

    logHttpBefore("GET projetos (lista)", { url: "/projetos", method: "get", params });
    const { data } = await api.get("/projetos", { params, signal: controller.signal });
    logHttpAfter("GET projetos (lista)", { data, status: 200, config: { url: "/projetos" } });

    const listRaw = data?.data || [];
    const list = listRaw.map((r) => ({ ...r, STATUS: normalizeStatus(r.STATUS) || "PENDENTE" }));

    setRows(list);
    setTotal(data?.total || 0);

    const creatorIds = Array.from(new Set(list.map((r) => Number(r.CREATED_BY)).filter(Boolean)));
    if (creatorIds.length) ensureNames(creatorIds);

    buildAssignedToMap(list);
  } catch (e) {
    if (e?.name === "CanceledError" || e?.message === "canceled") return;
    logHttpError("GET projetos (lista)", e);
    const info = axiosInfo(e);
    const det = info?.data?.details;
    const extra = Array.isArray(det) ? `: ${det.map((d) => d.message).join("; ")}` : "";
    showError((info?.data?.message || "Falha ao listar projetos") + extra);
  } finally {
    setLoading(false);
    lg.done("fetchList");
  }
};


  const rankAbortRef = useRef(null);
  const fetchRank = async () => {
    rankAbortRef.current?.abort?.();
    const controller = new AbortController();
    rankAbortRef.current = controller;

    if (!devId) {
      setRows([]);
      setTotal(0);
      showInfo("Selecione o técnico para ver a fila ranqueada.");
      return;
    }
    const lg = startLog("fetchRank", { devId });
    setLoading(true);
    try {
      let resp;
      try {
        const url = `/projetos/devs/${Number(devId)}/fila`;
        logHttpBefore("GET fila (novo)", { url, method: "get" });
        resp = await api.get(url, { signal: controller.signal });
        logHttpAfter("GET fila (novo)", resp);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.message === "canceled") return;
        logHttpError("GET fila (novo)", e);
        logHttpBefore("GET fila (legado)", {
          url: "/projetos/me",
          method: "get",
          params: { devId: Number(devId) },
        });
        resp = ProjetosApi.myQueue
          ? await ProjetosApi.myQueue({ devId: Number(devId) })
          : await api.get("/projetos/me", { params: { devId: Number(devId) } });
        logHttpAfter("GET fila (legado)", resp);
      }
      const raw = Array.isArray(resp?.data)
        ? resp.data
        : resp?.data?.data || [];
      const list = raw.map((r) => ({
        ...r,
        STATUS: normalizeStatus(r.STATUS) || "PENDENTE",
      }));
      setRows(list);
      setTotal(list.length);
      const m = {};
      list.forEach((r) => {
        m[r.ID_PROJETO] = Number(devId);
      });
      setAssignedToMap((prev) => ({ ...prev, ...m }));
      await ensureNames([Number(devId)]);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.message === "canceled") return;
      logHttpError("GET fila", e);
      const info = axiosInfo(e);
      showError(info?.data?.message || "Falha ao carregar a fila ranqueada.");
    } finally {
      setLoading(false);
      lg.done("fetchRank");
    }
  };

  useEffect(() => {
    if (view === "lista") fetchList();
    else fetchRank();
    // eslint-disable-next-line
  }, [view, tab, status, deferredQ, devId, page, limit, serverSort]);

  // sempre que serverSort mudar pelo select, persiste
  useEffect(() => {
    try {
      localStorage.setItem("PROJETOS_SERVER_SORT", serverSort);
    } catch {}
  }, [serverSort]);

  /* -------- Mapa de responsável principal -------- */
  const buildAssignedToMap = async (list) => {
    const principalMap = {};
    const idsToResolve = new Set();
    const needDetail = [];

    try {
      (list || []).forEach((project) => {
        const principalId =
          Number(project?.RESPONSAVEL_PRINCIPAL_ID) ||
          Number(
            getPrincipalParticipant(project)?.ID_DEV
          ) ||
          Number(project?.ID_DEV) ||
          Number(project?.ASSIGNEE_ID) ||
          null;

        if (principalId) {
          principalMap[project.ID_PROJETO] = principalId;
          idsToResolve.add(principalId);
        } else if (project?.ID_PROJETO) {
          needDetail.push(project.ID_PROJETO);
        }
      });

      if (needDetail.length) {
        let cursor = 0;
        const worker = async () => {
          while (cursor < needDetail.length) {
            const idProjeto = needDetail[cursor++];

            try {
              const { data } = await ProjetosApi.get(idProjeto);
              const principal = getPrincipalParticipant(data);
              const principalId = Number(principal?.ID_DEV) || null;

              principalMap[idProjeto] = principalId;
              if (principalId) idsToResolve.add(principalId);
            } catch (error) {
              logHttpError("GET projeto (responsável principal)", error);
              principalMap[idProjeto] = null;
            }
          }
        };

        await Promise.all(
          Array.from(
            { length: Math.min(6, needDetail.length) },
            () => worker()
          )
        );
      }

      setAssignedToMap(principalMap);
      if (idsToResolve.size) {
        ensureNames(Array.from(idsToResolve));
      }
    } catch (error) {
      console.error("[Projetos.buildAssignedToMap][ERR]", error);
    }
  };


  /* -------- Detalhe -------- */
  const fetchSessions = async (idProjeto) => {
    if (!idProjeto) return;

    setLoadingSessions(true);
    try {
      const response = ProjetosApi.listarSessoesTrabalho
        ? await ProjetosApi.listarSessoesTrabalho({
            idProjeto: Number(idProjeto),
            limit: 300,
          })
        : await api.get("/projetos/trabalho/sessoes", {
            params: { idProjeto: Number(idProjeto), limit: 300 },
          });

      const list = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || [];

      setSessions(list);

      const ids = list.map((item) => Number(item?.ID_DEV)).filter(Boolean);
      if (ids.length) ensureNames(ids);
    } catch (error) {
      console.error("[Projetos.fetchSessions][ERR]", error);
      setSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  const openDetail = async (id, tabInicial = "visao") => {
    const lg = startLog("openDetail", { id });

    try {
      logHttpBefore("GET projeto (detail)", {
        url: `/projetos/${id}`,
        method: "get",
      });

      const { data } = await ProjetosApi.get(id);

      logHttpAfter("GET projeto (detail)", {
        data,
        status: 200,
        config: { url: `/projetos/${id}` },
      });

      const normalized = data
        ? {
            ...data,
            STATUS: normalizeStatus(data.STATUS) || "PENDENTE",
          }
        : data;

      setDetail(normalized);
      setDetailTab(tabInicial || "visao");

      fetchComments(id);
      fetchAttachments(id);
      fetchSessions(id);

      const ids = new Set();
      const knownNames = {};

      const collectKnownPerson = (item, explicitId = null) => {
        if (!item && !explicitId) return;

        const id = Number(
          explicitId ??
            item?.ID_DEV ??
            item?.RESPONSAVEL_ID ??
            item?.RESPONSAVEL_RESOLUCAO_ID ??
            item?.CREATED_BY ??
            item?.RESOLVIDO_BY ??
            item?.AUTOR_ID ??
            item?.IdFuncionario ??
            item?.ID_FUNCIONARIO ??
            item?.ID ??
            item?.id
        );

        const name = pickName(item);

        if (id > 0) ids.add(id);
        if (id > 0 && name) knownNames[id] = String(name);
      };

      (data?.atribuidos || []).forEach((item) => {
        collectKnownPerson(item, item?.ID_DEV);
      });

      (data?.tarefas || []).forEach((item) => {
        collectKnownPerson(item, item?.RESPONSAVEL_ID);
      });

      (data?.bloqueios || []).forEach((item) => {
        collectKnownPerson(item, item?.RESPONSAVEL_RESOLUCAO_ID);
        collectKnownPerson(item, item?.CREATED_BY);
        collectKnownPerson(item, item?.RESOLVIDO_BY);
      });

      (data?.sessoesAtivas || []).forEach((item) => {
        collectKnownPerson(item, item?.ID_DEV);
      });

      collectKnownPerson(data, data?.CREATED_BY);
      collectKnownPerson(data, data?.GESTOR_PROJETO_ID);
      collectKnownPerson(data, data?.FORMALIZADO_BY);

      auditExtractPersonIds(data?.updates || []).forEach((personId) => {
        if (personId > 0) ids.add(personId);
      });

      if (Object.keys(knownNames).length) {
        mergeNames(knownNames);
      }

      if (ids.size) {
        await ensureNames(Array.from(ids));
      }

      const principal = getPrincipalParticipant(data);
      setAssignedToMap((prev) => ({
        ...prev,
        [id]: Number(principal?.ID_DEV) || null,
      }));
    } catch (error) {
      logHttpError("GET projeto (detail)", error);
      const info = axiosInfo(error);
      showError(info?.data?.message || "Falha ao abrir detalhes.");
    } finally {
      lg.done();
    }
  };


  /* -------- Comentários -------- */
  const fetchComments = async (projectId) => {
    setLoadingComments(true);
    try {
      const { data } = await api.get(`/projetos/${projectId}/comentarios`);
      const list = Array.isArray(data) ? data : data?.data || [];
      setComments(list);

      // garantir nomes por AUTOR_ID
      const ids = list.map((c) => Number(c?.AUTOR_ID)).filter(Boolean);
      if (ids.length) await ensureNames(ids);
    } catch (e) {
      showError(e?.response?.data?.message || "Falha ao carregar comentários.");
    } finally {
      setLoadingComments(false);
    }
  };

  const postComment = async () => {
  const projectId = getActiveProjectId();
  if (!projectId) return;

  const texto = (commentText || "").trim();
  if (!texto) return;

  const autorIdNum = Number(MY_ID) || null;

  const tempId = `tmp-${Date.now()}`;
  const optimistic = {
    ID_COMENTARIO: tempId,
    ID_PROJETO: projectId,
    AUTOR_ID: autorIdNum,
    TEXTO: texto,
    CREATED_AT: new Date().toISOString(),
    _optimistic: true,
  };

  setPostingComment(true);
  setComments((prev) => [optimistic, ...prev]);
  setCommentText("");

  try {
    const { data } = await api.post(
      `/projetos/${projectId}/comentarios`,
      { texto, autorId: autorIdNum || undefined }
    );

    if (data?.AUTOR_ID && pickName(data)) {
      mergeNames({ [Number(data.AUTOR_ID)]: String(pickName(data)) });
    }

    setComments((prev) =>
      prev.map((c) => (c.ID_COMENTARIO === tempId ? (data || c) : c))
    );

    if (autorIdNum) await ensureNames([autorIdNum]);

    showInfo("Comentário incluído.");
  } catch (e) {
    setComments((prev) => prev.filter((c) => c.ID_COMENTARIO !== tempId));
    setCommentText(texto);
    showError(e?.response?.data?.message || "Falha ao incluir comentário.");
  } finally {
    setPostingComment(false);
  }
};


 const deleteComment = async (c) => {
  const projectId = getActiveProjectId();
  if (!projectId || !c?.ID_COMENTARIO) return;
  if (!window.confirm("Remover este comentário?")) return;

  const snapshot = [...comments];
  setComments((prev) => prev.filter((x) => x.ID_COMENTARIO !== c.ID_COMENTARIO));

  try {
    await api.delete(`/projetos/${projectId}/comentarios/${c.ID_COMENTARIO}`);
    showInfo("Comentário removido.");
  } catch (e) {
    setComments(snapshot);
    showError(e?.response?.data?.message || "Falha ao remover comentário.");
  }
};


  const normalizeAttachmentsList = (list) =>
    (list || []).map((a) => ({
      id: a.ID_ANEXO ?? a.id ?? a.ID,
      name:
        a.NOME_ARQUIVO ??
        a.NOME ??
        a.nome ??
        a.filename ??
        `arquivo_${a.ID_ANEXO}`,
      // visualizar inline (abre no navegador)
      url: a.DISPLAY_URL ?? a.URL ?? a.url ?? a.display_url,
      // baixar com Content-Disposition: attachment
      downloadUrl: a.DOWNLOAD_URL ?? a.download_url ?? null,
      sizeHuman: a.TAMANHO_HUMANO ?? a.sizeHuman ?? null,
      contentType: a.CONTENT_TYPE ?? a.contentType ?? "",
    }));

  const fetchAttachments = async (projectId) => {
    setLoadingAttachments(true);
    try {
      const { data } = await api.get(`/projetos/${projectId}/anexos`);
      const list = Array.isArray(data) ? data : data?.data || [];
      setAttachments(normalizeAttachmentsList(list));
    } catch (e) {
      showError(e?.response?.data?.message || "Falha ao carregar anexos.");
    } finally {
      setLoadingAttachments(false);
    }
  };

  const uploadAttachment = async () => {
    if (!detail?.ID_PROJETO || !fileToUpload) return;
    const form = new FormData();
    form.append("file", fileToUpload); // o backend espera "file"

    setUploadingAttachment(true);
    try {
      // NÃO defina manualmente Content-Type aqui
      await api.post(`/projetos/${detail.ID_PROJETO}/anexos`, form);
      setFileToUpload(null);
      await fetchAttachments(detail.ID_PROJETO);
      showInfo("Anexo enviado.");
    } catch (e) {
      showError(e?.response?.data?.message || "Falha ao enviar anexo.");
    } finally {
      setUploadingAttachment(false);
    }
  };

  const deleteAttachment = async (anexo) => {
    // aceita { ID_ANEXO } ou um número direto
    const id = typeof anexo === "number" ? anexo : Number(anexo?.ID_ANEXO);
    if (!detail?.ID_PROJETO || !id) return;
    if (!window.confirm("Remover este anexo?")) return;

    // snapshot para rollback
    const snapshot = attachments;

    // 🔹 update otimista: some da lista imediatamente
    setAttachments((prev) => prev.filter((a) => a.id !== id));

    try {
      await api.delete(`/projetos/${detail.ID_PROJETO}/anexos/${id}`);
      showInfo("Anexo removido.");
    } catch (e) {
      // rollback se falhar
      setAttachments(snapshot);
      showError(e?.response?.data?.message || "Falha ao remover anexo.");
    }
  };

  /* -------- Editar (carrega dados e abre drawer) -------- */
  const openEdit = async (id) => {
    const lg = startLog("openEdit", { id });
    setEditId(id);
    setEditTab("form");
    setFormEdit({
      titulo: "",
      descricao: "",
      prioridade: "MEDIA",
      tipoProjeto: "PROJETO",
      cadastroProvisorio: false,
      gestorProjetoId: "",
      dtInicio: "",
      dtPrevista: "",
    });
    fetchComments(id);
    fetchAttachments(id);
    setFormEditErrors({});
    setEditDebug(null);
    setEditOpen(true);
    try {
      logHttpBefore("GET projeto (for edit)", {
        url: `/projetos/${id}`,
        method: "get",
      });
      const { data } = await ProjetosApi.get(id);
      logHttpAfter("GET projeto (for edit)", {
        data,
        status: 200,
        config: { url: `/projetos/${id}` },
      });
      setFormEdit({
        titulo: data?.TITULO || "",
        descricao: data?.DESCRICAO || "",
        prioridade: PRIORIDADES.includes(data?.PRIORIDADE)
          ? data.PRIORIDADE
          : "MEDIA",
        tipoProjeto: TIPOS_PROJETO.includes(data?.TIPO_PROJETO)
          ? data.TIPO_PROJETO
          : "PROJETO",
        cadastroProvisorio: isTrue(data?.CADASTRO_PROVISORIO),
        gestorProjetoId: data?.GESTOR_PROJETO_ID
          ? String(data.GESTOR_PROJETO_ID)
          : "",
        dtInicio: isoToLocalInput(data?.DT_INICIO),
        dtPrevista: isoToLocalInput(data?.DT_PREVISTA),
      });
    } catch (e) {
      logHttpError("GET projeto (for edit)", e);
      showError("Falha ao carregar dados para edição.");
      setEditOpen(false);
    } finally {
      lg.done();
    }
  };

  /* -------- Criar / Editar (inalterados) -------- */
  const submitCreate = async (e) => {
    e?.preventDefault();
    setFormCreateErrors({});
    if (!formCreate.titulo?.trim() || !formCreate.prioridade) {
      setFormCreateErrors({
        ...(formCreate.titulo?.trim()
          ? {}
          : { titulo: "Título é obrigatório" }),
        ...(formCreate.prioridade
          ? {}
          : { prioridade: "Prioridade é obrigatória" }),
      });
      showError("Título e Prioridade são obrigatórios.");
      return;
    }
   // Se houver 1 opção, use-a automaticamente
    const devIdImplicit =
      createCargo && !createDevId && createFuncs.length === 1
        ? String(createFuncs[0].id)
        : "";
    const chosenDevId = createDevId || devIdImplicit;
    if (createCargo && !chosenDevId) {
      showError("Selecione o funcionário para a atribuição inicial.");
      return;
    }
    const payload = {
      codigo: formCreate.codigo || undefined,
      titulo: formCreate.titulo.trim(),
      descricao: formCreate.descricao || undefined,
      prioridade: formCreate.prioridade,
      tipoProjeto: formCreate.tipoProjeto || "PROJETO",
      cadastroProvisorio: !!formCreate.cadastroProvisorio,
      gestorProjetoId:
        Number(MY_ID) > 0 && !formCreate.cadastroProvisorio
          ? Number(MY_ID)
          : undefined,
      dtInicio: toIsoWithSeconds(formCreate.dtInicio),
      dtPrevista: toIsoWithSeconds(formCreate.dtPrevista),
      createdBy: MY_ID || undefined,
    };

    setCreating(true);
    setCreateDebug({ request: payload, response: null, error: null });
    try {
      logHttpBefore("POST projeto (create)", {
        url: "/projetos",
        method: "post",
        payload,
      });
      const resp = await ProjetosApi.create(payload);
      logHttpAfter("POST projeto (create)", resp);
      const created = resp?.data;
      const newId = created?.ID_PROJETO ?? created?.id ?? created?.ID ?? null;
     if (newId && chosenDevId) {
        const devNum = Number(chosenDevId);
        const atrib = {
          devId: devNum,
          exclusivo: false,
          papel: "RESPONSAVEL_PRINCIPAL",
          responsavelPrincipal: true,
          podeMovimentar: true,
          ...(formCreate.descricao?.trim()?.length
            ? { justificativa: formCreate.descricao.trim() }
            : {}),
        };
        logHttpBefore("POST atribuir (on create)", {
          url: `/projetos/${newId}/atribuir`,
          method: "post",
          payload: atrib,
        });
        try {
          await api.post(`/projetos/${newId}/atribuir`, atrib, {
            headers: { "Content-Type": "application/json" },
          });
          logHttpAfter("POST atribuir (on create)", {
            status: 200,
            config: { url: `/projetos/${newId}/atribuir` },
          });
          setAssignedToMap((prev) => ({ ...prev, [newId]: devNum }));
          await ensureNames([devNum]);
        } catch (e2) {
          logHttpError("POST atribuir (on create)", e2);
        }
      }
      if (newId && createFiles.length) {
        for (const file of createFiles) {
          const form = new FormData();
          form.append("file", file);
          try {
            await api.post(`/projetos/${newId}/anexos`, form);
          } catch (ex) {
            logHttpError("POST anexo (on create)", ex);
          }
        }
      }
      setCreateDebug({
        request: payload,
        response: resp?.data ?? true,
        error: null,
      });
      showInfo("Projeto criado.");
      setCreateOpen(false);
      setFormCreate({
        codigo: "",
        titulo: "",
        descricao: "",
        prioridade: "MEDIA",
        tipoProjeto: "PROJETO",
        cadastroProvisorio: false,
        dtInicio: "",
        dtPrevista: "",
      });
      setCreateCargo("");
      setCreateDevId("");
      setCreateFiles([]);
    } catch (e2) {
      logHttpError("POST projeto (create)", e2);
      const info = axiosInfo(e2);
      setCreateDebug({ request: payload, response: info?.data, error: info });
      const det = info?.data?.details || info?.data?.error?.details;
      const fieldErrors = {};
      if (Array.isArray(det))
        det.forEach((d) => {
          const key = d?.context?.key || d?.path?.[0];
          if (key) fieldErrors[key] = d?.message || "Inválido";
        });
      if (Object.keys(fieldErrors).length) setFormCreateErrors(fieldErrors);
      const extra = Array.isArray(det)
        ? `: ${det.map((d) => d.message).join("; ")}`
        : "";
      showError((info?.data?.message || "Falha ao criar projeto.") + extra);
    } finally {
      setCreating(false);
    }
  };

  const updateProjeto = async (id, payload) => {
    if (ProjetosApi?.update) {
      try {
        logHttpBefore("UPDATE projeto (SDK)", {
          url: `/projetos/${id}`,
          method: "put",
          payload,
        });
        const resp = await ProjetosApi.update(id, payload);
        logHttpAfter("UPDATE projeto (SDK)", resp);
        return resp;
      } catch (e) {
        logHttpError("UPDATE projeto (SDK)", e);
      }
    }
    try {
      logHttpBefore("PATCH projeto", {
        url: `/projetos/${id}`,
        method: "patch",
        payload,
      });
      const resp = await api.patch(`/projetos/${id}`, payload);
      logHttpAfter("PATCH projeto", resp);
      return resp;
    } catch (e1) {
      logHttpError("PATCH projeto", e1);
      logHttpBefore("PUT projeto (fallback)", {
        url: `/projetos/${id}`,
        method: "put",
        payload,
      });
      const resp2 = await api.put(`/projetos/${id}`, payload);
      logHttpAfter("PUT projeto (fallback)", resp2);
      return resp2;
    }
  };

  async function updateStatus(projId, { status, progresso, descricao } = {}) {
    const body = {};
    if (status) {
      const canon = normalizeStatus(status); // UPPERCASE
      if (canon) body.status = canon;
    }
    if (typeof progresso === "number") {
      body.progresso = Math.max(0, Math.min(100, Math.round(progresso)));
    }
    if (descricao !== undefined) body.descricao = String(descricao || "");
    return api.patch(`/projetos/${projId}/status`, body);
  }

  const submitEdit = async (e) => {
    e?.preventDefault();
    setFormEditErrors({});
    if (!formEdit.titulo?.trim() || !formEdit.prioridade) {
      setFormEditErrors({
        ...(formEdit.titulo?.trim() ? {} : { titulo: "Título é obrigatório" }),
        ...(formEdit.prioridade
          ? {}
          : { prioridade: "Prioridade é obrigatória" }),
      });
      showError("Título e Prioridade são obrigatórios.");
      return;
    }
    const payload = {
      titulo: formEdit.titulo.trim(),
      descricao: formEdit.descricao || null,
      prioridade: formEdit.prioridade,
      tipoProjeto: formEdit.tipoProjeto || "PROJETO",
      cadastroProvisorio: !!formEdit.cadastroProvisorio,
      gestorProjetoId: formEdit.gestorProjetoId
        ? Number(formEdit.gestorProjetoId)
        : null,
      ...(formEdit.dtInicio
        ? { dtInicio: toIsoWithSeconds(formEdit.dtInicio) }
        : { dtInicio: null }),
      ...(formEdit.dtPrevista
        ? { dtPrevista: toIsoWithSeconds(formEdit.dtPrevista) }
        : { dtPrevista: null }),
    };

    setEditing(true);
    setEditDebug({ request: payload, response: null, error: null });
    try {
      const resp = await updateProjeto(editId, payload);
      setEditDebug((p) => ({ ...(p || {}), response: resp?.data ?? true }));
      showInfo("Projeto atualizado.");
      setEditOpen(false);
      if (view === "lista") await fetchList();
      else await fetchRank();
      if (detail?.ID_PROJETO === editId) await openDetail(editId);
    } catch (e2) {
      const info = axiosInfo(e2);
      setEditDebug((p) => ({ ...(p || {}), error: info }));
      const det = info?.data?.details || info?.data?.error?.details;
      const fieldErrors = {};
      if (Array.isArray(det))
        det.forEach((d) => {
          const key = d?.context?.key || d?.path?.[0];
          if (key) fieldErrors[key] = d?.message || "Inválido";
        });
      if (Object.keys(fieldErrors).length) setFormEditErrors(fieldErrors);
      const extra = Array.isArray(det)
        ? `: ${det.map((d) => d.message).join("; ")}`
        : "";
      showError((info?.data?.message || "Falha ao atualizar projeto.") + extra);
    } finally {
      setEditing(false);
    }
  };

  /* ==================== ATRIBUIR / TROCAR + FILA ==================== */
  useEffect(() => {
    if (assignOpen) {
      setAssignDevId("");
      setAssignJust("");
      setAssignStatus("");
      setAssignRole("DESENVOLVEDOR");
      setAssignPrincipal(false);
      setAssignPodeMovimentar(true);
      setAssignExclusive(false);
      setSelectedCargo("");
      setFuncOptions([]);
  
      if (!cargos.length) fetchCargos();
    }
    // eslint-disable-next-line
  }, [assignOpen]);
  useEffect(() => {
    if (selectedCargo) fetchFuncsByCargo(selectedCargo, "assign");
  }, [selectedCargo]); // eslint-disable-line
  /*
   * A escolha de técnico é sempre deliberada.
   * Não selecionar automaticamente a primeira ou única opção.
   */



  // const loadQueueForDev = async (dev) => {
  //   const devNum = Number(dev || 0);
  //   if (!assignOpen || !devNum) {
  //     setQueueItems([]);
  //     setQueueFocusId(null);
  //     setQueueOriginalFocusId(null);
  //     setQueueDirty(false);
  //     setQueueInserted(false);
  //     return;
  //   }
  //   setQueueLoading(true);
  //   try {
  //     let resp;
  //     try {
  //       const url = `/projetos/devs/${devNum}/fila`;
  //       logHttpBefore("GET fila do dev (novo)", { url, method: "get" });
  //       resp = await api.get(url);
  //       logHttpAfter("GET fila do dev (novo)", resp);
  //     } catch (e) {
  //       logHttpError("GET fila do dev (novo)", e);
  //       logHttpBefore("GET fila (legado)", {
  //         url: "/projetos/me",
  //         method: "get",
  //         params: { devId: devNum },
  //       });
  //       resp = ProjetosApi.myQueue
  //         ? await ProjetosApi.myQueue({ devId: devNum })
  //         : await api.get("/projetos/me", { params: { devId: devNum } });
  //       logHttpAfter("GET fila (legado)", resp);
  //     }
  //     const list = Array.isArray(resp?.data)
  //       ? resp.data
  //       : resp?.data?.data || [];
  //     const focusItem = list.find((x) => x.FOCUS_ATIVO);
  //     const focusId = focusItem?.ID_PROJETO ?? null;

  //     let final = [...list];
  //     if (assignFor && !final.some((x) => x.ID_PROJETO === assignFor)) {
  //       const base = rows.find((x) => x.ID_PROJETO === assignFor) || {};
  //       final = [
  //         {
  //           ID_PROJETO: assignFor,
  //           TITULO: base.TITULO || `(novo) #${assignFor}`,
  //           PRIORIDADE: base.PRIORIDADE || "MEDIA",
  //           DT_PREVISTA: base.DT_PREVISTA || null,
  //           SCORE: null,
  //           QT_COMENTARIOS: null,
  //           QT_ANEXOS: null,
  //           FOCUS_ATIVO: false,
  //           __INSERTED__: true,
  //         },
  //         ...final,
  //       ];
  //       setQueueInserted(true);
  //       setQueueDirty(true);
  //     } else setQueueInserted(false);

  //     setQueueItems(final);
  //     setQueueFocusId(focusId);
  //     setQueueOriginalFocusId(focusId);
  //   } catch (e) {
  //     logHttpError("GET fila do dev", e);
  //     showError(
  //       e?.response?.data?.message || "Falha ao carregar fila do técnico."
  //     );
  //     setQueueItems([]);
  //     setQueueFocusId(null);
  //     setQueueOriginalFocusId(null);
  //   } finally {
  //     setQueueLoading(false);
  //   }
  // };

  // const moveItem = (idx, dir) => {
  // //  setQueueItems((prev) => {
  //     const arr = [...prev];
  //     const j = dir === "up" ? idx - 1 : idx + 1;
  //     if (j < 0 || j >= arr.length) return prev;
  //     [arr[idx], arr[j]] = [arr[j], arr[idx]];
  //     return arr;
  //   });
  //   setQueueDirty(true);
  // };

  const applyAssignOnly = async () => {
    const lg = startLog("applyAssignOnly", {
      assignFor,
      assignDevId,
      assignJust,
      assignStatus,
      assignExclusive,
    });

    if (!assignFor) {
      showError("Projeto não selecionado.");
      lg.done("projeto ausente");
      return;
    }

    const devNum = Number(assignDevId || 0);
    if (!devNum) {
      showError("Selecione o técnico que será incluído.");
      lg.done("técnico ausente");
      return;
    }

    const canonStatus = assignStatus && normalizeStatus(assignStatus);
    const projetoAtribuicao =
      rows.find(
        (item) => Number(item.ID_PROJETO) === Number(assignFor)
      ) ||
      (Number(detail?.ID_PROJETO) === Number(assignFor)
        ? detail
        : null);

    if (
      isProjetoOriginadoDeChamado(projetoAtribuicao) &&
      canonStatus &&
      STATUS_BLOQUEADOS_PROJETO_CHAMADO.has(canonStatus)
    ) {
      showError(
        "Projetos originados de chamados não podem ser finalizados ou concluídos durante a atribuição."
      );
      lg.done("status bloqueado");
      return;
    }

    const activeParticipantIds = new Set(
      Number(detail?.ID_PROJETO) === Number(assignFor)
        ? getActiveParticipants(detail).map((item) =>
            Number(item.ID_DEV)
          )
        : []
    );

    if (!assignExclusive && activeParticipantIds.has(devNum)) {
      showError(
        "Este técnico já participa do projeto. Altere papel e permissões na aba Equipe."
      );
      lg.done("participante já ativo");
      return;
    }

    setApplying(true);

    try {
      const justificativa = assignJust?.trim() || null;
      const autorId = Number(MY_ID) || undefined;

      if (assignExclusive) {
        const payloadExclusivo = {
          devId: devNum,
          exclusivo: true,
          papel: "RESPONSAVEL_PRINCIPAL",
          responsavelPrincipal: true,
          podeMovimentar: !!assignPodeMovimentar,
          transferirFoco: false,
          autorId,
          ...(justificativa ? { justificativa } : {}),
          ...(canonStatus ? { status: canonStatus } : {}),
        };

        logHttpBefore("POST substituir equipe", {
          url: `/projetos/${assignFor}/atribuir`,
          method: "post",
          payload: payloadExclusivo,
        });

        const response = await api.post(
          `/projetos/${assignFor}/atribuir`,
          payloadExclusivo,
          { headers: { "Content-Type": "application/json" } }
        );

        logHttpAfter("POST substituir equipe", response);
      } else {
        const payloadParticipante = {
          devId: devNum,
          papel: assignPrincipal
            ? "RESPONSAVEL_PRINCIPAL"
            : assignRole,
          responsavelPrincipal: !!assignPrincipal,
          podeMovimentar: !!assignPodeMovimentar,
          autorId,
          ...(justificativa ? { justificativa } : {}),
        };

        logHttpBefore("POST participante", {
          url: `/projetos/${assignFor}/participantes`,
          method: "post",
          payload: payloadParticipante,
        });

        const response = ProjetosApi.adicionarParticipante
          ? await ProjetosApi.adicionarParticipante(
              assignFor,
              payloadParticipante
            )
          : await api.post(
              `/projetos/${assignFor}/participantes`,
              payloadParticipante,
              { headers: { "Content-Type": "application/json" } }
            );

        logHttpAfter("POST participante", response);

        if (canonStatus) {
          const payloadStatus = {
            status: canonStatus,
            descricao:
              justificativa ||
              `Status atualizado após inclusão do técnico #${devNum}.`,
            autorId,
          };

          if (ProjetosApi.patchStatus) {
            await ProjetosApi.patchStatus(assignFor, payloadStatus);
          } else {
            await api.patch(
              `/projetos/${assignFor}/status`,
              payloadStatus
            );
          }
        }
      }

      if (
        assignPrincipal ||
        assignExclusive ||
        !assignedToMap[assignFor]
      ) {
        setAssignedToMap((previous) => ({
          ...previous,
          [assignFor]: devNum,
        }));
      }

      const selectedParticipant = funcOptions.find(
        (option) => Number(option.id) === devNum
      );

      if (selectedParticipant?.label) {
        mergeNames({
          [devNum]: String(selectedParticipant.label),
        });
      } else {
        await ensureNames([devNum]);
      }

      showInfo(
        assignExclusive
          ? `${
              selectedParticipant?.label || getName(devNum)
            } foi definido como responsável principal.`
          : `${
              selectedParticipant?.label || getName(devNum)
            } foi adicionado ao projeto.`
      );

      const projectId = Number(assignFor);

      setAssignOpen(false);
      setAssignFor(null);
      setSelectedCargo("");
      setAssignDevId("");
      setFuncOptions([]);

      if (view === "lista") await fetchList();
      else await fetchRank();

      if (Number(detail?.ID_PROJETO) === projectId) {
        await openDetail(projectId, "equipe");
      }
    } catch (error) {
      logHttpError("Adicionar participante", error);

      const details = error?.response?.data?.details;
      const detailMessage = Array.isArray(details)
        ? details
            .map((item) => item?.message || item)
            .filter(Boolean)
            .join(" | ")
        : "";

      showError(
        detailMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Falha ao adicionar participante."
      );
    } finally {
      setApplying(false);
      lg.done();
    }
  };

  const submitAssign = async (e) => {
    e?.preventDefault?.();
    await applyAssignOnly();
  };

  /* ===== NOVO: fluxo Gerenciar Fila ===== */
  const openManageQueueFor = async (dev) => {
    const did = Number(dev || 0);
    if (!did) {
      showError("Este projeto não possui responsável ativo.");
      return;
    }
    setManageDevId(did);
    setManageDevName(getName(did));
    setManageQueueItems([]);
    setManageQueueFocusId(null);
    setManageQueueOriginalFocusId(null);
    setManageQueueDirty(false);
    setManageOpen(true);
    await loadManageQueue(did);
  };

  const loadManageQueue = async (dev) => {
    const devNum = Number(dev || 0);
    if (!devNum) return;
    setManageQueueLoading(true);
    try {
      let resp;
      try {
        const url = `/projetos/devs/${devNum}/fila`;
        logHttpBefore("GET fila (manage)", { url, method: "get" });
        resp = await api.get(url);
        logHttpAfter("GET fila (manage)", resp);
      } catch (e) {
        logHttpError("GET fila (manage novo)", e);
        logHttpBefore("GET fila (manage legado)", {
          url: "/projetos/me",
          method: "get",
          params: { devId: devNum },
        });
        resp = ProjetosApi.myQueue
          ? await ProjetosApi.myQueue({ devId: devNum })
          : await api.get("/projetos/me", { params: { devId: devNum } });
        logHttpAfter("GET fila (manage legado)", resp);
      }
      const list = Array.isArray(resp?.data)
        ? resp.data
        : resp?.data?.data || [];
      const focusItem = list.find((x) => x.FOCUS_ATIVO);
      setManageQueueItems(list);
      setManageQueueFocusId(focusItem?.ID_PROJETO ?? null);
      setManageQueueOriginalFocusId(focusItem?.ID_PROJETO ?? null);
    } catch (e) {
      logHttpError("GET fila (manage)", e);
      showError(
        e?.response?.data?.message || "Falha ao carregar fila do técnico."
      );
      setManageQueueItems([]);
      setManageQueueFocusId(null);
      setManageQueueOriginalFocusId(null);
    } finally {
      setManageQueueLoading(false);
    }
  };

  const moveManageItem = (idx, dir) => {
    setManageQueueItems((prev) => {
      const arr = [...prev];
      const j = dir === "up" ? idx - 1 : idx + 1;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
    setManageQueueDirty(true);
  };

  const applyManageQueue = async () => {
    const devNum = Number(manageDevId || 0);
    if (!devNum) return;
    setManageApplying(true);
    try {
      if (manageQueueItems.length > 0 && manageQueueDirty) {
        const ids = manageQueueItems.map((x) => x.ID_PROJETO);
        try {
          logHttpBefore("PATCH fila (manage novo)", {
            url: `/projetos/devs/${devNum}/fila`,
            method: "patch",
            payload: { ids },
          });
          const resp = await api.patch(`/projetos/devs/${devNum}/fila`, {
            ids,
          });
          logHttpAfter("PATCH fila (manage novo)", resp);
        } catch (e) {
          logHttpError("PATCH fila (manage)", e);
          if (e?.response?.status === 404) {
            logHttpBefore("POST fila (manage legado)", {
              url: "/projetos/fila",
              method: "post",
              payload: { devId: devNum, ids },
            });
            const resp2 = await api.post("/projetos/fila", {
              devId: devNum,
              ids,
            });
            logHttpAfter("POST fila (manage legado)", resp2);
          } else {
            const det = e?.response?.data?.details;
            const extra = Array.isArray(det)
              ? `: ${det.map((d) => d.message).join("; ")}`
              : "";
            showError(
              (e?.response?.data?.message ||
                "Falha ao salvar a fila do técnico") + extra
            );
            return;
          }
        }
      }

      const desiredFocus = manageQueueFocusId;
      const original = manageQueueOriginalFocusId;
      if (desiredFocus !== original) {
        if (desiredFocus) {
          const payload = { devId: devNum, ativo: true, motivo: "ADM" };
          logHttpBefore("POST foco (manage ativar)", {
            url: `/projetos/${desiredFocus}/foco`,
            method: "post",
            payload,
          });
          const resp = ProjetosApi.foco
            ? await ProjetosApi.foco(desiredFocus, payload)
            : await api.post(`/projetos/${desiredFocus}/foco`, payload);
          logHttpAfter("POST foco (manage ativar)", resp);
        } else if (original) {
          const payload = { devId: devNum, ativo: false };
          logHttpBefore("POST foco (manage remover)", {
            url: `/projetos/${original}/foco`,
            method: "post",
            payload,
          });
          const resp = ProjetosApi.foco
            ? await ProjetosApi.foco(original, payload)
            : await api.post(`/projetos/${original}/foco`, payload);
          logHttpAfter("POST foco (manage remover)", resp);
        }
      }

      showInfo("Fila e foco atualizados.");
      setManageOpen(false);
      if (view === "rank" && String(devId) === String(devNum))
        await fetchRank();
      if (view === "lista") await fetchList();
    } finally {
      setManageApplying(false);
    }
  };

  const refreshCurrentProject = async (tabToKeep = detailTab) => {
    const idProjeto = Number(detail?.ID_PROJETO || 0);
    if (!idProjeto) return;

    await openDetail(idProjeto, tabToKeep);
    if (view === "lista") await fetchList();
    else await fetchRank();
  };

  const formalizarProjeto = async (project) => {
    const idProjeto = Number(project?.ID_PROJETO || 0);
    if (!idProjeto) return;

    const confirmed = window.confirm(
      "Confirmar a formalização deste cadastro provisório? O histórico e o tempo já registrado serão mantidos."
    );

    if (!confirmed) return;

    setOperationalBusy(true);
    try {
      await updateProjeto(idProjeto, {
        cadastroProvisorio: false,
        gestorProjetoId: Number(MY_ID) || null,
      });

      showInfo("Projeto formalizado com sucesso.");

      if (detail?.ID_PROJETO === idProjeto) {
        await openDetail(idProjeto, detailTab);
      }

      if (view === "lista") await fetchList();
      else await fetchRank();
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          "Falha ao formalizar o projeto provisório."
      );
    } finally {
      setOperationalBusy(false);
    }
  };

  const atualizarParticipanteProjeto = async (dev, patch) => {
    const idProjeto = Number(detail?.ID_PROJETO || 0);
    const participanteId = Number(dev || 0);
    if (!idProjeto || !participanteId) return;

    setOperationalBusy(true);
    try {
      const payload = {
        ...patch,
        autorId: Number(MY_ID) || undefined,
      };

      if (ProjetosApi.atualizarParticipante) {
        await ProjetosApi.atualizarParticipante(
          idProjeto,
          participanteId,
          payload
        );
      } else {
        await api.patch(
          `/projetos/${idProjeto}/participantes/${participanteId}`,
          payload
        );
      }

      showInfo("Participante atualizado.");
      await refreshCurrentProject("equipe");
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          "Falha ao atualizar o participante."
      );
    } finally {
      setOperationalBusy(false);
    }
  };

  const removerParticipanteProjeto = async (dev) => {
    const idProjeto = Number(detail?.ID_PROJETO || 0);
    const participanteId = Number(dev || 0);
    if (!idProjeto || !participanteId) return;

    const confirmed = window.confirm(
      `Remover ${getName(participanteId)} da equipe deste projeto? O histórico será preservado.`
    );
    if (!confirmed) return;

    setOperationalBusy(true);
    try {
      const payload = {
        justificativa: "Removido pela gestão de projetos.",
        autorId: Number(MY_ID) || undefined,
      };

      if (ProjetosApi.removerParticipante) {
        await ProjetosApi.removerParticipante(
          idProjeto,
          participanteId,
          payload
        );
      } else {
        await api.delete(
          `/projetos/${idProjeto}/participantes/${participanteId}`,
          { data: payload }
        );
      }

      showInfo("Participante removido da equipe.");
      await refreshCurrentProject("equipe");
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          "Falha ao remover o participante."
      );
    } finally {
      setOperationalBusy(false);
    }
  };

  const criarTarefaProjeto = async (event) => {
    event?.preventDefault?.();

    const idProjeto = Number(detail?.ID_PROJETO || 0);
    if (!idProjeto) return;

    const titulo = formTask.titulo.trim();
    if (!titulo) {
      showError("Informe o título da tarefa.");
      return;
    }

    setOperationalBusy(true);
    try {
      const payload = {
        titulo,
        descricao: formTask.descricao.trim() || null,
        estimativaH:
          formTask.estimativaH === ""
            ? null
            : Number(formTask.estimativaH),
        responsavelId: formTask.responsavelId
          ? Number(formTask.responsavelId)
          : null,
        dtPrevista: formTask.dtPrevista || null,
      };

      if (ProjetosApi.criarTarefa) {
        await ProjetosApi.criarTarefa(idProjeto, payload);
      } else {
        await api.post(`/projetos/${idProjeto}/tarefas`, payload);
      }

      setFormTask({
        titulo: "",
        descricao: "",
        estimativaH: "",
        responsavelId: "",
        dtPrevista: "",
      });

      showInfo("Tarefa criada.");
      await refreshCurrentProject("tarefas");
    } catch (error) {
      showError(
        error?.response?.data?.message || "Falha ao criar a tarefa."
      );
    } finally {
      setOperationalBusy(false);
    }
  };

  const atualizarTarefaProjeto = async (idTarefa, patch) => {
    const idProjeto = Number(detail?.ID_PROJETO || 0);
    if (!idProjeto || !idTarefa) return;

    setOperationalBusy(true);
    try {
      if (ProjetosApi.atualizarTarefa) {
        await ProjetosApi.atualizarTarefa(idProjeto, idTarefa, patch);
      } else {
        await api.patch(
          `/projetos/${idProjeto}/tarefas/${idTarefa}`,
          patch
        );
      }

      showInfo("Tarefa atualizada.");
      await refreshCurrentProject("tarefas");
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          "Falha ao atualizar a tarefa."
      );
    } finally {
      setOperationalBusy(false);
    }
  };

  const criarBloqueioProjeto = async (event) => {
    event?.preventDefault?.();

    const idProjeto = Number(detail?.ID_PROJETO || 0);
    if (!idProjeto) return;

    if (!formBlock.motivo.trim()) {
      showError("Informe o motivo do bloqueio.");
      return;
    }

    setOperationalBusy(true);
    try {
      const payload = {
        idTarefa: formBlock.idTarefa
          ? Number(formBlock.idTarefa)
          : null,
        tipoBloqueio: formBlock.tipoBloqueio,
        motivo: formBlock.motivo.trim(),
        proximaAcao: formBlock.proximaAcao.trim() || null,
        responsavelResolucaoId: formBlock.responsavelResolucaoId
          ? Number(formBlock.responsavelResolucaoId)
          : null,
        dtProximaRevisao: formBlock.dtProximaRevisao || null,
        createdBy: Number(MY_ID) || undefined,
        autorId: Number(MY_ID) || undefined,
      };

      if (ProjetosApi.criarBloqueio) {
        await ProjetosApi.criarBloqueio(idProjeto, payload);
      } else {
        await api.post(`/projetos/${idProjeto}/bloqueios`, payload);
      }

      setFormBlock({
        idTarefa: "",
        tipoBloqueio: "OUTRO",
        motivo: "",
        proximaAcao: "",
        responsavelResolucaoId: "",
        dtProximaRevisao: "",
      });

      showInfo("Bloqueio registrado.");
      await refreshCurrentProject("bloqueios");
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          "Falha ao registrar o bloqueio."
      );
    } finally {
      setOperationalBusy(false);
    }
  };

  const resolverBloqueioProjeto = async () => {
    const idProjeto = Number(detail?.ID_PROJETO || 0);
    const idBloqueio = Number(resolucaoBloqueio.idBloqueio || 0);
    const solucao = resolucaoBloqueio.texto.trim();

    if (!idProjeto || !idBloqueio) return;
    if (!solucao) {
      showError("Informe como o bloqueio foi resolvido.");
      return;
    }

    setOperationalBusy(true);
    try {
      const payload = {
        solucao,
        resolvidoBy: Number(MY_ID) || undefined,
        autorId: Number(MY_ID) || undefined,
      };

      if (ProjetosApi.resolverBloqueio) {
        await ProjetosApi.resolverBloqueio(
          idProjeto,
          idBloqueio,
          payload
        );
      } else {
        await api.post(
          `/projetos/${idProjeto}/bloqueios/${idBloqueio}/resolver`,
          payload
        );
      }

      setResolucaoBloqueio({ idBloqueio: null, texto: "" });
      showInfo("Bloqueio resolvido.");
      await refreshCurrentProject("bloqueios");
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          "Falha ao resolver o bloqueio."
      );
    } finally {
      setOperationalBusy(false);
    }
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / limit)),
    [total, limit]
  );
  const MY_ID = useMemo(() => getLoggedUserId(), []);
  const funcionarioLogado = useMemo(
    () => getSessionValue("RazaoSocial", "Funcionario", "funcionario"),
    [],
  );
  const codFuncLogado = useMemo(
    () => getSessionValue("codFunc", "CodFunc", "CODFUNC"),
    [],
  );
  const autorIdLogado = useMemo(
    () => Number(getSessionValue("IdFuncionario", "Idfuncionario", "codFunc", "CodFunc")) || 0,
    [],
  );

  const projectOverview = useMemo(() => {
    const base = Array.isArray(rows) ? rows : [];

    const originados = base.filter(isProjetoOriginadoDeChamado).length;

    const aprovados = base.filter(
      (item) => normalizeStatus(item.STATUS) === STATUS.TESTADO_APROVADO
    ).length;

    const aguardandoCliente = base.filter(
      (item) =>
        normalizeStatus(item.STATUS) === STATUS.AGUARDANDO_RETORNO_CLIENTE
    ).length;

    const emAndamento = base.filter(
      (item) => normalizeStatus(item.STATUS) === STATUS.EM_ANDAMENTO
    ).length;

    const colaborativos = base.filter(
      (item) => Number(item.QT_PARTICIPANTES || 0) > 1
    ).length;

    const provisorios = base.filter((item) =>
      isTrue(item.CADASTRO_PROVISORIO)
    ).length;

    const sessoesAtivas = base.reduce(
      (totalAtual, item) =>
        totalAtual +
        Math.max(
          0,
          Number(
            item.QT_SESSOES_ATIVAS ||
              (item.SESSAO_ATIVA_ID ? 1 : 0)
          )
        ),
      0
    );

    const atrasados = base.filter((item) => {
      if (!item.DT_PREVISTA) return false;

      const statusItem = normalizeStatus(item.STATUS);
      if (
        [
          STATUS.CONCLUIDO,
          STATUS.CANCELADO,
          STATUS.AGUARDANDO_RETORNO_CLIENTE,
        ].includes(statusItem)
      ) {
        return false;
      }

      return new Date(item.DT_PREVISTA).getTime() < Date.now();
    }).length;

    return {
      total: base.length,
      originados,
      aprovados,
      aguardandoCliente,
      emAndamento,
      colaborativos,
      provisorios,
      sessoesAtivas,
      atrasados,
    };
  }, [rows]);

  const EmptyState = () => (
    <div className="empty-state">
      <div className="empty-title">Nenhum projeto encontrado</div>
      <div className="empty-sub">
        Ajuste os filtros ou cadastre um novo projeto.
      </div>
      <Button onClick={() => setCreateOpen(true)} style={{ marginTop: 8 }}>
        Criar projeto
      </Button>
    </div>
  );

  const QuickFilters = () => (
    <div className="quick-filters">
      <button
        type="button"
        className={`chip ${status === "" ? "active" : ""}`}
        onClick={() => {
          setStatus("");
          setPage(1);
        }}
      >
        Todos
      </button>
      {STATUS_FOR_FILTER.map((s) => (
        <button
          key={s}
          type="button"
          className={`chip ${status === s ? "active" : ""}`}
          onClick={() => {
            setStatus(s);
            setPage(1);
          }}
        >
          {STATUS_LABEL[s]}
        </button>
      ))}
    </div>
  );

  // catálogos para filtros
  useEffect(() => {
    fetchCargos();
  }, []); // eslint-disable-line
  useEffect(() => {
    if (filterCargo) fetchFuncsByCargo(filterCargo, "filter");
    else {
      setFilterFuncs([]);
      setDevId("");
    }
    // eslint-disable-next-line
  }, [filterCargo]);

  if (DEBUG)
    window.__ProjetosAdmin = {
      fetchList,
      fetchRank,
      setStatus,
      setQ: setQ,
      setDevId,
      setPage,
      setLimit,
      setServerSort,
      setView,
      openEdit,
    };

  /* ==================== Derivados (filtro por aba + filtros locais + sort) ==================== */
  const filteredByTab = useMemo(() => {
    let list = Array.isArray(rows) ? [...rows] : [];

    if (view === "lista") {
      if (tab === "abertos") {
        list = list.filter(
          (item) =>
            item.STATUS !== STATUS.CONCLUIDO &&
            item.STATUS !== STATUS.CANCELADO
        );
      }

      if (tab === "concluidos") {
        list = list.filter((item) => item.STATUS === STATUS.CONCLUIDO);
      }

      if (tab === "cancelados") {
        list = list.filter((item) => item.STATUS === STATUS.CANCELADO);
      }
    }

    if (tipoProjetoFiltro) {
      list = list.filter(
        (item) =>
          String(item.TIPO_PROJETO || "PROJETO").toUpperCase() ===
          tipoProjetoFiltro
      );
    }

    if (situacaoEspecial === "COLABORATIVO") {
      list = list.filter(
        (item) => Number(item.QT_PARTICIPANTES || 0) > 1
      );
    }

    if (situacaoEspecial === "PROVISORIO") {
      list = list.filter((item) =>
        isTrue(item.CADASTRO_PROVISORIO)
      );
    }

    if (situacaoEspecial === "COM_SESSAO_ATIVA") {
      list = list.filter(
        (item) => Number(item.QT_SESSOES_ATIVAS || 0) > 0
      );
    }

    if (situacaoEspecial === "SEM_RESPONSAVEL") {
      list = list.filter(
        (item) =>
          !Number(item.RESPONSAVEL_PRINCIPAL_ID || 0) &&
          Number(item.QT_PARTICIPANTES || 0) === 0
      );
    }

    return list;
  }, [
    rows,
    view,
    tab,
    tipoProjetoFiltro,
    situacaoEspecial,
  ]);




  // accessors para sort local
  const accessors = useMemo(
    () => ({
      ID: (r) => Number(r.ID_PROJETO) || 0,
      TITULO: (r) => r.TITULO || "",
      ATRIBUIDO_A: (r) =>
        getName(
          Number(r.RESPONSAVEL_PRINCIPAL_ID) ||
            assignedToMap[r.ID_PROJETO] ||
            null
        ) || "",
      TIPO_PROJETO: (r) =>
        TIPO_PROJETO_LABEL[r.TIPO_PROJETO] || r.TIPO_PROJETO || "Projeto",
      QT_PARTICIPANTES: (r) => Number(r.QT_PARTICIPANTES || 0),
      QT_SESSOES_ATIVAS: (r) => Number(r.QT_SESSOES_ATIVAS || 0),
      STATUS: (r) => STATUS_LABEL[r.STATUS] || "",
      PRIORIDADE: (r) => PRIORIDADE_WEIGHT[r.PRIORIDADE] ?? -1,
      PROGRESSO: (r) => Number(r.PROGRESSO) || 0,
      DT_PREVISTA: (r) => getDate(r.DT_PREVISTA),
      FOCO: (r) => (r.FOCUS_ATIVO ? 1 : 0),
      SCORE: (r) => Number(r.SCORE ?? -Infinity),
      QT_COMENTARIOS: (r) => Number(r.QT_COMENTARIOS ?? 0),
      QT_ANEXOS: (r) => Number(r.QT_ANEXOS ?? 0),
      CREATED_AT: (r) => getDate(r.CREATED_AT),
      CRIADO_POR: (r) => getName(Number(r.CREATED_BY)) || "",
      // eslint-disable-next-line
    }),
    [assignedToMap, getName]
  );

  const displayedRows = useMemo(
    () => sortRows(filteredByTab, localSort, accessors),
    [filteredByTab, localSort, accessors]
  );

  const toggleLocalSort = (key, serverFieldIfAny) => {
    // calcula a próxima direção antes de setar o estado
    const isNewKey = localSort.key !== key;
    // padrão: 'asc', mas para PRIORIDADE queremos começar em 'desc' (maior peso primeiro)
    const nextDir = isNewKey
      ? key === "PRIORIDADE"
        ? "desc"
        : "asc"
      : localSort.dir === "asc"
      ? "desc"
      : "asc";

    const next = { key, dir: nextDir };
    setLocalSort(next);
    // persiste no localStorage
    try {
      localStorage.setItem("PROJETOS_LOCAL_SORT", JSON.stringify(next));
    } catch {}

    if (serverFieldIfAny) {
      const nextServer = `${serverFieldIfAny}:${nextDir}`;
      setServerSort(nextServer);
      try {
        localStorage.setItem("PROJETOS_SERVER_SORT", nextServer);
      } catch {}
    }
  };

  const parseSearch = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return { id: null, text: "" };
  const m = s.match(/^#?(\d+)$/);
  if (m) return { id: Number(m[1]), text: "" };
  return { id: null, text: s };
};


  const sortIcon = (key) =>
    localSort.key !== key ? "↕" : localSort.dir === "asc" ? "↑" : "↓";

  /* ==================== JSX ==================== */
  return (
    <MainLayout>
      <div className="proj-page projects-admin-page">
        {/* Toolbar */}
        <div className="proj-toolbar-admin projects-page-header">
          <div className="left">
            <span className="proj-eyebrow">Gestão de desenvolvimento</span>
            <h1>Projetos</h1>
            <p className="proj-toolbar-subtitle">
              Priorize demandas, acompanhe a execução e controle o retorno ao cliente.
            </p>
          </div>
          <div className="right" style={{ display: "flex", gap: 8 }}>
            <Button
              variant="outline"
              onClick={() => (view === "lista" ? fetchList() : fetchRank())}
            >
              Atualizar lista
            </Button>
            <div className="segmented">
              <button
                className={view === "lista" ? "active" : ""}
                onClick={() => setView("lista")}
                title="Lista com filtros"
              >
                Lista
              </button>
              <button
                className={view === "rank" ? "active" : ""}
                onClick={() => setView("rank")}
                title="Minha Fila ranqueada"
              >
                Minha Fila (rank)
              </button>
            </div>
            <Button onClick={() => setCreateOpen(true)}>Novo Projeto</Button>
          </div>
        </div>

        <section
          className="project-overview-grid"
          aria-label="Resumo dos projetos"
        >
          <article className="project-overview-card total">
            <span>Projetos carregados</span>
            <strong>{projectOverview.total}</strong>
            <small>Resultado da página atual</small>
          </article>

          <article className="project-overview-card running">
            <span>Em andamento</span>
            <strong>{projectOverview.emAndamento}</strong>
            <small>Projetos em execução</small>
          </article>

          <article className="project-overview-card collaborative">
            <span>Colaborativos</span>
            <strong>{projectOverview.colaborativos}</strong>
            <small>Mais de um participante</small>
          </article>

          <article className="project-overview-card sessions">
            <span>Sessões ativas</span>
            <strong>{projectOverview.sessoesAtivas}</strong>
            <small>Desenvolvedores trabalhando agora</small>
          </article>

          <article className="project-overview-card provisional">
            <span>Provisórios</span>
            <strong>{projectOverview.provisorios}</strong>
            <small>Precisam ser formalizados</small>
          </article>

          <article className="project-overview-card origin">
            <span>Vindos de chamados</span>
            <strong>{projectOverview.originados}</strong>
            <small>Fluxo com retorno obrigatório</small>
          </article>

          <article className="project-overview-card approved">
            <span>Prontos para suporte</span>
            <strong>{projectOverview.aprovados}</strong>
            <small>Testados e aprovados</small>
          </article>

          <article className="project-overview-card waiting">
            <span>Aguardando cliente</span>
            <strong>{projectOverview.aguardandoCliente}</strong>
            <small>Retorno em poder do suporte</small>
          </article>

          <article className="project-overview-card overdue">
            <span>Atrasados</span>
            <strong>{projectOverview.atrasados}</strong>
            <small>Prazo previsto vencido</small>
          </article>
        </section>

        {view === "lista" && (
          <div className="tabs projects-status-tabs">
            <button
              className={`tab ${tab === "abertos" ? "active" : ""}`}
              onClick={() => {
                setTab("abertos");
                setPage(1);
              }}
            >
              Abertos
            </button>
            <button
              className={`tab ${tab === "concluidos" ? "active" : ""}`}
              onClick={() => {
                setTab("concluidos");
                setPage(1);
              }}
            >
              Concluídos
            </button>
            <button
              className={`tab ${tab === "cancelados" ? "active" : ""}`}
              onClick={() => {
                setTab("cancelados");
                setPage(1);
              }}
            >
              Cancelados
            </button>
          </div>
        )}

        {/* Filtros */}
        <div className="card filters projects-filters-card">
          <div className="projects-filter-header">
            <div>
              <span className="projects-section-eyebrow">
                Consulta e organização
              </span>
              <h2>
                {view === "lista"
                  ? "Localizar projetos"
                  : "Fila priorizada do técnico"}
              </h2>
              <p>
                {view === "lista"
                  ? "Combine status, tipo, situação, setor e responsável para encontrar rapidamente o projeto necessário."
                  : "Selecione o setor e o técnico para visualizar a ordem de execução calculada pelo sistema."}
              </p>
            </div>

            <span className="projects-results-pill">
              {loading
                ? "Carregando..."
                : view === "lista"
                ? `${displayedRows.length} de ${total} projeto(s)`
                : `${displayedRows.length} projeto(s) na fila`}
            </span>
          </div>

          <div className="grid grid-4">
            {view === "lista" && (
              <>
                <div>
                  <Label>Status</Label>
                  <select
                    className="input native"
                    value={status}
                    onChange={(e) => {
                      setPage(1);
                      setStatus(e.target.value);
                    }}
                  >
                    <option value="">(todos)</option>
                    {Object.values(STATUS)
                      .filter(
                        (s) =>
                          tab !== "abertos" ||
                          (s !== STATUS.CONCLUIDO && s !== STATUS.CANCELADO)
                      )
                      .map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABEL[s]}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <Label>Tipo</Label>
                  <select
                    className="input native"
                    value={tipoProjetoFiltro}
                    onChange={(event) => {
                      setTipoProjetoFiltro(event.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">(todos os tipos)</option>
                    {TIPOS_PROJETO.map((item) => (
                      <option key={item} value={item}>
                        {TIPO_PROJETO_LABEL[item]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Situação especial</Label>
                  <select
                    className="input native"
                    value={situacaoEspecial}
                    onChange={(event) => {
                      setSituacaoEspecial(event.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">(todas)</option>
                    <option value="COLABORATIVO">Colaborativos</option>
                    <option value="PROVISORIO">Cadastros provisórios</option>
                    <option value="COM_SESSAO_ATIVA">Com trabalho ativo</option>
                    <option value="SEM_RESPONSAVEL">Sem responsável</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <Label>Busca</Label>
                  <Input
                    placeholder="Título ou código"
                    value={q}
                    onChange={(e) => {
                      setPage(1);
                      setQ(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") fetchList();
                    }}
                  />
                </div>

                <div>
                  <Label>Ordenação (servidor)</Label>
                  <select
                    className="input native"
                    value={serverSort}
                    onChange={(e) => setServerSort(e.target.value)}
                  >
                    <option value="DT_PREVISTA:asc">DT_PREVISTA:asc</option>
                    <option value="DT_PREVISTA:desc">DT_PREVISTA:desc</option>
                    <option value="PRIORIDADE:desc">PRIORIDADE:desc</option>
                    <option value="PRIORIDADE:asc">PRIORIDADE:asc</option>
                    <option value="TITULO:asc">TITULO:asc</option>
                    <option value="TITULO:desc">TITULO:desc</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <Label>Setor</Label>
                  <select
                    className="input native"
                    value={filterCargo}
                    onChange={(e) => setFilterCargo(e.target.value)}
                  >
                    <option value="">
                      {loadingCargos ? "Carregando..." : "(todos os setores)"}
                    </option>
                    {cargos.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <Label>Funcionário (filtrar por responsável)</Label>
                  <select
                    className="input native"
                    value={devId}
                    onChange={(e) => {
                      setDevId(e.target.value);
                      setPage(1);
                    }}
                    disabled={!filterCargo || loadingFilterFuncs}
                  >
                    {!filterCargo && (
                      <option value="">(selecione primeiro o setor)</option>
                    )}
                    {filterCargo && loadingFilterFuncs && (
                      <option value="">Carregando...</option>
                    )}
                    {filterCargo && !loadingFilterFuncs && (
                      <>
                        <option value="">(qualquer responsável)</option>
                        {filterFuncs.map((opt) => (
                          <option key={opt.id} value={String(opt.id)}>
                            {opt.label}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
              </>
            )}

            {view === "rank" && (
              <>
                <div className="col-span-2">
                  <Label>Setor</Label>
                  <select
                    className="input native"
                    value={filterCargo}
                    onChange={(e) => setFilterCargo(e.target.value)}
                  >
                    <option value="">
                      {loadingCargos ? "Carregando..." : "(selecione)"}
                    </option>
                    {cargos.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <Label>Técnico (obrigatório)</Label>
                  <select
                    className="input native"
                    value={devId}
                    onChange={(e) => setDevId(e.target.value)}
                    disabled={!filterCargo || loadingFilterFuncs}
                  >
                    {!filterCargo && (
                      <option value="">(selecione primeiro o setor)</option>
                    )}
                    {filterCargo && loadingFilterFuncs && (
                      <option value="">Carregando...</option>
                    )}
                    {filterCargo &&
                      !loadingFilterFuncs &&
                      filterFuncs.map((opt) => (
                        <option key={opt.id} value={String(opt.id)}>
                          {opt.label}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="col-span-4">
                  <Label>Observação</Label>
                  <div className="hint">
                    A lista é ranqueada pelo serviço (FOCO ativo, SCORE, prazo,
                    idade).
                  </div>
                </div>
              </>
            )}
          </div>
          {view === "lista" && tab === "abertos" && <QuickFilters />}
        </div>

        {/* Tabela */}
        <div className="card projects-table-card">
          <div className="projects-table-heading">
            <div>
              <span className="projects-section-eyebrow">
                Carteira de projetos
              </span>
              <h2>
                {view === "lista" ? "Projetos encontrados" : "Ranking da fila"}
              </h2>
              <p>
                Consulte o responsável principal, trabalho ativo, status,
                prioridade, prazo e ações disponíveis.
              </p>
            </div>

            <div className="projects-table-count">
              <strong>{displayedRows.length}</strong>
              <span>exibidos</span>
            </div>
          </div>

          <div className="table-wrap">
            <table className="table-sm w-full">
              <thead className="sticky">
                <tr>
                  <th style={{ width: 72 }}>
                    <button
                      className="th-sort"
                      onClick={() => toggleLocalSort("ID", "ID_PROJETO")}
                    >
                      ID {sortIcon("ID")}
                    </button>
                  </th>

                  <th style={{ minWidth: 300 }}>
                    <button
                      className="th-sort"
                      onClick={() => toggleLocalSort("TITULO", "TITULO")}
                    >
                      Projeto {sortIcon("TITULO")}
                    </button>
                  </th>

                  <th style={{ minWidth: 140 }}>
                    <button
                      className="th-sort"
                      onClick={() => toggleLocalSort("TIPO_PROJETO")}
                    >
                      Tipo {sortIcon("TIPO_PROJETO")}
                    </button>
                  </th>

                  <th style={{ minWidth: 210 }}>
                    <button
                      className="th-sort"
                      onClick={() => toggleLocalSort("ATRIBUIDO_A")}
                    >
                      Equipe {sortIcon("ATRIBUIDO_A")}
                    </button>
                  </th>

                  <th style={{ minWidth: 110 }}>
                    <button
                      className="th-sort"
                      onClick={() => toggleLocalSort("QT_SESSOES_ATIVAS")}
                    >
                      Trabalho {sortIcon("QT_SESSOES_ATIVAS")}
                    </button>
                  </th>

                  <th>
                    <button
                      className="th-sort"
                      onClick={() => toggleLocalSort("STATUS")}
                    >
                      Status {sortIcon("STATUS")}
                    </button>
                  </th>

                  <th>
                    <button
                      className="th-sort"
                      onClick={() =>
                        toggleLocalSort("PRIORIDADE", "PRIORIDADE")
                      }
                    >
                      Prioridade {sortIcon("PRIORIDADE")}
                    </button>
                  </th>

                  <th>
                    <button
                      className="th-sort"
                      onClick={() => toggleLocalSort("PROGRESSO")}
                    >
                      Progresso {sortIcon("PROGRESSO")}
                    </button>
                  </th>

                  <th>
                    <button
                      className="th-sort"
                      onClick={() =>
                        toggleLocalSort("DT_PREVISTA", "DT_PREVISTA")
                      }
                    >
                      Prevista {sortIcon("DT_PREVISTA")}
                    </button>
                  </th>

                  {view === "rank" && (
                    <>
                      <th>
                        <button
                          className="th-sort"
                          onClick={() => toggleLocalSort("FOCO")}
                        >
                          Foco {sortIcon("FOCO")}
                        </button>
                      </th>

                      <th>
                        <button
                          className="th-sort"
                          onClick={() => toggleLocalSort("SCORE")}
                        >
                          Score {sortIcon("SCORE")}
                        </button>
                      </th>

                      <th>
                        <button
                          className="th-sort"
                          onClick={() => toggleLocalSort("QT_COMENTARIOS")}
                        >
                          Comentários {sortIcon("QT_COMENTARIOS")}
                        </button>
                      </th>

                      <th>
                        <button
                          className="th-sort"
                          onClick={() => toggleLocalSort("QT_ANEXOS")}
                        >
                          Anexos {sortIcon("QT_ANEXOS")}
                        </button>
                      </th>
                    </>
                  )}

                  <th>
                    <button
                      className="th-sort"
                      onClick={() =>
                        toggleLocalSort("CREATED_AT", "CREATED_AT")
                      }
                    >
                      Criado em {sortIcon("CREATED_AT")}
                    </button>
                  </th>

                  <th>
                    <button
                      className="th-sort"
                      onClick={() => toggleLocalSort("CRIADO_POR")}
                    >
                      Criado por {sortIcon("CRIADO_POR")}
                    </button>
                  </th>

                  <th style={{ minWidth: 470 }}>Ações</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={view === "rank" ? 16 : 12}>Carregando…</td>
                  </tr>
                ) : displayedRows.length === 0 ? (
                  <tr>
                    <td colSpan={view === "rank" ? 16 : 12}>
                      <EmptyState />
                    </td>
                  </tr>
                ) : (
                  displayedRows.map((r) => {
                    const principalId =
                      Number(r.RESPONSAVEL_PRINCIPAL_ID) ||
                      Number(assignedToMap[r.ID_PROJETO]) ||
                      null;

                    const principalName = getName(principalId);
                    const participantCount = Math.max(
                      Number(r.QT_PARTICIPANTES || 0),
                      principalId ? 1 : 0
                    );

                    //const isAssigned = participantCount > 0;
                    const isCollaborative = participantCount > 1;
                    const sessionCount = Number(
                      r.QT_SESSOES_ATIVAS ||
                        (r.SESSAO_ATIVA_ID ? 1 : 0)
                    );
                    const isProvisional = isTrue(
                      r.CADASTRO_PROVISORIO
                    );
                    const projectType =
                      r.TIPO_PROJETO || "PROJETO";

                    return (
                      <tr
                        key={r.ID_PROJETO}
                        className={[
                          isProjetoOriginadoDeChamado(r)
                            ? "project-row-origin"
                            : "",
                          normalizeStatus(r.STATUS) ===
                          STATUS.AGUARDANDO_RETORNO_CLIENTE
                            ? "project-row-waiting"
                            : "",
                          normalizeStatus(r.STATUS) ===
                          STATUS.TESTADO_APROVADO
                            ? "project-row-approved"
                            : "",
                          isProvisional
                            ? "project-row-provisional"
                            : "",
                          sessionCount > 0
                            ? "project-row-working"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <td className="mono">#{r.ID_PROJETO}</td>

                        <td>
                          <div className="project-title-stack">
                            <strong>{r.TITULO}</strong>

                            <div className="project-title-meta">
                              {isProjetoOriginadoDeChamado(r) ? (
                                <span className="project-origin-chip">
                                  Chamado #{r.ID_CHAMADO_ORIGEM}
                                </span>
                              ) : (
                                <span className="project-internal-chip">
                                  Projeto interno
                                </span>
                              )}

                              {isProvisional && (
                                <span className="project-provisional-chip">
                                  Cadastro provisório
                                </span>
                              )}

                              {isCollaborative && (
                                <span className="project-collaborative-chip">
                                  Colaborativo
                                </span>
                              )}

                              {r.CODIGO ? (
                                <span className="mono">{r.CODIGO}</span>
                              ) : null}
                            </div>
                          </div>
                        </td>

                        <td>
                          <span
                            className={`project-type-chip type-${String(
                              projectType
                            ).toLowerCase()}`}
                          >
                            {TIPO_PROJETO_LABEL[projectType] ||
                              projectType}
                          </span>
                        </td>

                        <td>
                          <div className="project-team-cell">
                            <strong>
                              {principalId
                                ? principalName
                                : "Sem responsável principal"}
                            </strong>
                            <small>
                              {participantCount === 0
                                ? "Nenhum participante"
                                : participantCount === 1
                                ? "1 participante"
                                : `${participantCount} participantes`}
                            </small>
                          </div>
                        </td>

                        <td>
                          {sessionCount > 0 ? (
                            <span className="work-session-badge">
                              <span className="work-session-dot" />
                              {sessionCount === 1
                                ? "1 ativa"
                                : `${sessionCount} ativas`}
                            </span>
                          ) : (
                            <span className="muted">Sem sessão</span>
                          )}
                        </td>

                        <td>
                          <EditableStatusCell
                            project={r}
                            id={r.ID_PROJETO}
                            status={r.STATUS}
                            onChange={(newStatus) => {
                              setRows((prev) =>
                                prev.map((item) =>
                                  item.ID_PROJETO === r.ID_PROJETO
                                    ? {
                                        ...item,
                                        STATUS: newStatus,
                                      }
                                    : item
                                )
                              );
                            }}
                          />
                        </td>

                        <td>
                          <PriorityBadge value={r.PRIORIDADE} />
                        </td>

                        <td>{r.PROGRESSO}%</td>

                        <td>
                          {r.DT_PREVISTA
                            ? new Date(
                                r.DT_PREVISTA
                              ).toLocaleDateString()
                            : "-"}
                        </td>

                        {view === "rank" && (
                          <>
                            <td>
                              {r.FOCUS_ATIVO ? (
                                <span className="badge badge-purple">
                                  FOCO
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>{r.SCORE ?? "-"}</td>
                            <td>{r.QT_COMENTARIOS ?? "-"}</td>
                            <td>{r.QT_ANEXOS ?? "-"}</td>
                          </>
                        )}

                        <td>
                          {r.CREATED_AT
                            ? new Date(r.CREATED_AT).toLocaleString()
                            : "-"}
                        </td>

                        <td>{getName(Number(r.CREATED_BY))}</td>

                        <td>
                          <div className="row-actions">
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setAssignFor(r.ID_PROJETO);
                                setAssignOpen(true);
                              }}
                            >
                              Adicionar à equipe
                            </Button>

                            <Button
                              variant="outline"
                              onClick={() =>
                                openDetail(r.ID_PROJETO, "equipe")
                              }
                            >
                              Equipe
                            </Button>

                            <Button
                              variant="outline"
                              onClick={() => openEdit(r.ID_PROJETO)}
                            >
                              Editar
                            </Button>

                            <Button
                              onClick={() =>
                                openDetail(r.ID_PROJETO, "visao")
                              }
                            >
                              Detalhes
                            </Button>

                            {isProvisional && (
                              <Button
                                variant="secondary"
                                disabled={operationalBusy}
                                onClick={() => formalizarProjeto(r)}
                              >
                                Formalizar
                              </Button>
                            )}

                            <Button
                              variant="secondary"
                              onClick={() =>
                                openManageQueueFor(principalId)
                              }
                              disabled={!principalId}
                              title={
                                principalId
                                  ? `Gerenciar fila de ${principalName}`
                                  : "Projeto sem responsável principal"
                              }
                            >
                              Fila principal
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginação */}
        {view === "lista" && (
          <div className="pager projects-pager">
            <div>
              <Label>Linhas/página</Label>{" "}
              <select
                className="input native"
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="ml-auto">
              <Button
                variant="secondary"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </Button>
              <span style={{ margin: "0 8px" }}>
                Página {page} de {totalPages}
              </span>
              <Button
                variant="secondary"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}

        {/* Drawer: Detalhe */}
        {detail && (
          <div
            className="projects-modal-backdrop project-detail-modal-backdrop"
            role="dialog"
            aria-modal="true"
            onClick={() => setDetail(null)}
          >
            <div
              className="projects-modal projects-modal-xl project-detail-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="projects-modal-header project-detail-header">
                <div className="project-detail-heading">
                  <div className="project-detail-heading-line">
                    <span className="mono">
                      #{detail.ID_PROJETO}
                    </span>
                    <span
                      className={`project-type-chip type-${String(
                        detail.TIPO_PROJETO || "PROJETO"
                      ).toLowerCase()}`}
                    >
                      {TIPO_PROJETO_LABEL[
                        detail.TIPO_PROJETO || "PROJETO"
                      ] || detail.TIPO_PROJETO}
                    </span>

                    {isTrue(detail.CADASTRO_PROVISORIO) && (
                      <span className="project-provisional-chip">
                        Provisório
                      </span>
                    )}
                  </div>

                  <h2>{detail.TITULO}</h2>

                  <p>
                    {getActiveParticipants(detail).length === 0
                      ? "Projeto sem participantes ativos"
                      : `${getActiveParticipants(detail).length} participante(s) ativo(s)`}
                    {" • "}
                    {(detail.sessoesAtivas || []).length} sessão(ões)
                    ativa(s)
                  </p>
                </div>

                <div className="project-detail-header-actions">
                  {isTrue(detail.CADASTRO_PROVISORIO) && (
                    <Button
                      variant="secondary"
                      disabled={operationalBusy}
                      onClick={() => formalizarProjeto(detail)}
                    >
                      Formalizar
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    onClick={() => openEdit(detail.ID_PROJETO)}
                  >
                    Editar
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={() => setDetail(null)}
                  >
                    Fechar
                  </Button>
                </div>
              </div>

              <div className="projects-modal-content project-detail-content">
                {isProjetoOriginadoDeChamado(detail) ? (
                  <div className="project-flow-detail">
                    <ProjetoFluxoChamado
                      projeto={detail}
                      funcionario={funcionarioLogado}
                      codFunc={codFuncLogado}
                      autorId={autorIdLogado}
                      onUpdated={async () => {
                        await openDetail(
                          detail.ID_PROJETO,
                          detailTab
                        );

                        if (view === "lista") await fetchList();
                        else await fetchRank();
                      }}
                    />
                  </div>
                ) : null}

                {isTrue(detail.CADASTRO_PROVISORIO) && (
                  <div className="project-provisional-alert">
                    <div>
                      <strong>Cadastro provisório de atendimento</strong>
                      <span>
                        O trabalho e o tempo já estão sendo registrados.
                        Formalize o projeto para completar escopo, gestor,
                        previsão e equipe sem perder o histórico.
                      </span>
                    </div>

                    <Button
                      disabled={operationalBusy}
                      onClick={() => formalizarProjeto(detail)}
                    >
                      Formalizar agora
                    </Button>
                  </div>
                )}

                <div className="tabs project-detail-tabs">
                  {[
                    ["visao", "Visão geral"],
                    ["equipe", `Equipe (${getActiveParticipants(detail).length})`],
                    ["tarefas", `Tarefas (${(detail.tarefas || []).length})`],
                    [
                      "bloqueios",
                      `Bloqueios (${(detail.bloqueios || []).filter(
                        (item) => item.STATUS_BLOQUEIO === "ABERTO"
                      ).length})`,
                    ],
                    ["tempo", `Tempo (${sessions.length})`],
                    ["comentarios", `Comentários (${comments.length})`],
                    ["anexos", `Anexos (${attachments.length})`],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      className={`tab ${
                        detailTab === key ? "active" : ""
                      }`}
                      onClick={() => setDetailTab(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {detailTab === "visao" && (
                  <div className="project-detail-section">
                    <div className="grid grid-3 detail-summary-grid">
                      <div>
                        <span>Status</span>
                        <StatusBadge value={detail.STATUS} />
                      </div>

                      <div>
                        <span>Prioridade</span>
                        <PriorityBadge value={detail.PRIORIDADE} />
                      </div>

                      <div>
                        <span>Progresso</span>
                        <strong>{detail.PROGRESSO || 0}%</strong>
                      </div>

                      <div>
                        <span>Responsável principal</span>
                        <strong>
                          {getPrincipalParticipant(detail)
                            ? getName(
                                getPrincipalParticipant(detail).ID_DEV
                              )
                            : "Não definido"}
                        </strong>
                      </div>

                      <div>
                        <span>Gestor do projeto</span>
                        <strong>
                          {detail.GESTOR_PROJETO_ID
                            ? getName(detail.GESTOR_PROJETO_ID)
                            : "Não definido"}
                        </strong>
                      </div>

                      <div>
                        <span>Equipe ativa</span>
                        <strong>
                          {getActiveParticipants(detail).length}
                        </strong>
                      </div>

                      <div>
                        <span>Início</span>
                        <strong>
                          {detail.DT_INICIO
                            ? new Date(
                                detail.DT_INICIO
                              ).toLocaleString()
                            : "-"}
                        </strong>
                      </div>

                      <div>
                        <span>Previsão</span>
                        <strong>
                          {detail.DT_PREVISTA
                            ? new Date(
                                detail.DT_PREVISTA
                              ).toLocaleString()
                            : "-"}
                        </strong>
                      </div>

                      <div>
                        <span>Trabalhando agora</span>
                        <strong>
                          {(detail.sessoesAtivas || []).length}
                        </strong>
                      </div>
                    </div>

                    <section className="project-detail-card">
                      <div className="section-title-row">
                        <div>
                          <h3>Descrição e escopo</h3>
                          <p>
                            Contexto principal preservado no projeto.
                          </p>
                        </div>
                      </div>

                      <div className="desc">
                        {detail.DESCRICAO || "Sem descrição informada."}
                      </div>
                    </section>

                    {(detail.sessoesAtivas || []).length > 0 && (
                      <section className="project-detail-card">
                        <div className="section-title-row">
                          <div>
                            <h3>Trabalhando agora</h3>
                            <p>
                              Sessões ativas registradas no banco.
                            </p>
                          </div>
                        </div>

                        <div className="active-session-grid">
                          {detail.sessoesAtivas.map((session) => (
                            <article
                              key={session.ID_SESSAO}
                              className="active-session-card"
                            >
                              <div className="active-session-person">
                                <span className="work-session-dot" />
                                <div>
                                  <strong>
                                    {getName(session)}
                                  </strong>
                                  <small>
                                    {session.TAREFA_TITULO ||
                                      "Atividade geral do projeto"}
                                  </small>
                                </div>
                              </div>

                              <span className="session-timer">
                                {formatDuration(
                                  getElapsedSessionSeconds(session)
                                )}
                              </span>
                            </article>
                          ))}
                        </div>
                      </section>
                    )}

                    <section className="project-detail-card">
                      <div className="section-title-row">
                        <div>
                          <h3>Últimas movimentações</h3>
                          <p>
                            Auditoria mais recente do projeto.
                          </p>
                        </div>
                      </div>

                      {(detail.updates || []).length === 0 ? (
                        <div className="empty-sub">
                          Nenhuma movimentação registrada.
                        </div>
                      ) : (
                        <div className="project-timeline">
                          {(detail.updates || [])
                            .slice(0, 15)
                            .map((update) => (
                              <AuditMovement
                                key={update.ID_UPDATE}
                                update={update}
                                resolveName={getName}
                              />
                            ))}
                        </div>
                      )}
                    </section>
                  </div>
                )}

                {detailTab === "equipe" && (
                  <div className="project-detail-section">
                    <div className="section-title-row">
                      <div>
                        <h3>Equipe colaborativa</h3>
                        <p>
                          Cada participante possui papel, permissão e
                          responsabilidade próprias.
                        </p>
                      </div>

                      <Button
                        onClick={() => {
                          setAssignFor(detail.ID_PROJETO);
                          setAssignOpen(true);
                        }}
                      >
                        Adicionar participante
                      </Button>
                    </div>

                    <div className="project-participant-list">
                      {getActiveParticipants(detail).length === 0 ? (
                        <div className="empty-state">
                          <div className="empty-title">
                            Nenhum participante ativo
                          </div>
                          <div className="empty-sub">
                            Adicione o responsável principal e os
                            colaboradores.
                          </div>
                        </div>
                      ) : (
                        getActiveParticipants(detail).map(
                          (participant) => (
                            <article
                              key={participant.ID_ATRIBUICAO}
                              className={`project-participant-card ${
                                isTrue(
                                  participant.RESPONSAVEL_PRINCIPAL
                                )
                                  ? "is-principal"
                                  : ""
                              } ${
                                isTrue(
                                  participant.TRABALHANDO_AGORA
                                )
                                  ? "is-working"
                                  : ""
                              }`}
                            >
                              <div className="participant-identity">
                                <div className="participant-avatar">
                                  {String(
                                    getName(participant)
                                  )
                                    .trim()
                                    .charAt(0)
                                    .toUpperCase()}
                                </div>

                                <div>
                                  <strong>
                                    {getName(participant)}
                                  </strong>

                                  <span className="mono">
                                    #{participant.ID_DEV}
                                  </span>

                                  <div className="participant-flags">
                                    {isTrue(
                                      participant.RESPONSAVEL_PRINCIPAL
                                    ) && (
                                      <span className="principal-chip">
                                        Principal
                                      </span>
                                    )}

                                    {isTrue(
                                      participant.TRABALHANDO_AGORA
                                    ) && (
                                      <span className="working-chip">
                                        Trabalhando agora
                                      </span>
                                    )}

                                    {!isTrue(
                                      participant.PODE_MOVIMENTAR
                                    ) && (
                                      <span className="readonly-chip">
                                        Somente acompanhamento
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="participant-controls">
                                <div>
                                  <Label>Papel</Label>
                                  <select
                                    className="input native"
                                    value={
                                      participant.PAPEL ||
                                      "DESENVOLVEDOR"
                                    }
                                    disabled={operationalBusy}
                                    onChange={(event) =>
                                      atualizarParticipanteProjeto(
                                        participant.ID_DEV,
                                        {
                                          papel: event.target.value,
                                        }
                                      )
                                    }
                                  >
                                    {PAPEIS_PROJETO.map((item) => (
                                      <option
                                        key={item}
                                        value={item}
                                      >
                                        {PAPEL_PROJETO_LABEL[item]}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <label className="participant-check">
                                  <input
                                    type="checkbox"
                                    checked={isTrue(
                                      participant.RESPONSAVEL_PRINCIPAL
                                    )}
                                    disabled={operationalBusy}
                                    onChange={(event) =>
                                      atualizarParticipanteProjeto(
                                        participant.ID_DEV,
                                        {
                                          responsavelPrincipal:
                                            event.target.checked,
                                        }
                                      )
                                    }
                                  />
                                  <span>Responsável principal</span>
                                </label>

                                <label className="participant-check">
                                  <input
                                    type="checkbox"
                                    checked={isTrue(
                                      participant.PODE_MOVIMENTAR
                                    )}
                                    disabled={operationalBusy}
                                    onChange={(event) =>
                                      atualizarParticipanteProjeto(
                                        participant.ID_DEV,
                                        {
                                          podeMovimentar:
                                            event.target.checked,
                                        }
                                      )
                                    }
                                  />
                                  <span>Pode movimentar</span>
                                </label>

                                <Button
                                  variant="secondary"
                                  disabled={
                                    operationalBusy ||
                                    isTrue(
                                      participant.TRABALHANDO_AGORA
                                    )
                                  }
                                  onClick={() =>
                                    removerParticipanteProjeto(
                                      participant.ID_DEV
                                    )
                                  }
                                >
                                  Remover da equipe
                                </Button>

                                <Button
                                  variant="outline"
                                  onClick={() =>
                                    openManageQueueFor(
                                      participant.ID_DEV
                                    )
                                  }
                                >
                                  Gerenciar fila
                                </Button>
                              </div>
                            </article>
                          )
                        )
                      )}
                    </div>

                    {(detail.atribuidos || []).some(
                      (item) => !isTrue(item?.ATIVO)
                    ) && (
                      <details className="inactive-participants">
                        <summary>
                          Histórico de participantes inativos
                        </summary>

                        <ul className="list">
                          {(detail.atribuidos || [])
                            .filter(
                              (item) => !isTrue(item?.ATIVO)
                            )
                            .map((item) => (
                              <li key={item.ID_ATRIBUICAO}>
                                {getName(item)} —{" "}
                                {PAPEL_PROJETO_LABEL[item.PAPEL] ||
                                  item.PAPEL ||
                                  "Participante"}{" "}
                                — removido em{" "}
                                {item.PARTICIPA_ATE
                                  ? new Date(
                                      item.PARTICIPA_ATE
                                    ).toLocaleString()
                                  : "-"}
                              </li>
                            ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}

                {detailTab === "tarefas" && (
                  <div className="project-detail-section">
                    <section className="project-detail-card">
                      <div className="section-title-row">
                        <div>
                          <h3>Nova tarefa</h3>
                          <p>
                            Divida a entrega e atribua cada parte a um
                            participante.
                          </p>
                        </div>
                      </div>

                      <form
                        className="grid grid-2 project-task-form"
                        onSubmit={criarTarefaProjeto}
                      >
                        <div>
                          <Label>Título *</Label>
                          <Input
                            value={formTask.titulo}
                            onChange={(event) =>
                              setFormTask((previous) => ({
                                ...previous,
                                titulo: event.target.value,
                              }))
                            }
                            placeholder="Ex.: Criar endpoint de consulta"
                          />
                        </div>

                        <div>
                          <Label>Responsável</Label>
                          <select
                            className="input native"
                            value={formTask.responsavelId}
                            onChange={(event) =>
                              setFormTask((previous) => ({
                                ...previous,
                                responsavelId:
                                  event.target.value,
                              }))
                            }
                          >
                            <option value="">
                              Sem responsável
                            </option>

                            {getActiveParticipants(detail).map(
                              (participant) => (
                                <option
                                  key={participant.ID_DEV}
                                  value={participant.ID_DEV}
                                >
                                  {getName(participant)}
                                </option>
                              )
                            )}
                          </select>
                        </div>

                        <div>
                          <Label>Estimativa em horas</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.25"
                            value={formTask.estimativaH}
                            onChange={(event) =>
                              setFormTask((previous) => ({
                                ...previous,
                                estimativaH:
                                  event.target.value,
                              }))
                            }
                          />
                        </div>

                        <div>
                          <Label>Previsão</Label>
                          <Input
                            type="datetime-local"
                            value={formTask.dtPrevista}
                            onChange={(event) =>
                              setFormTask((previous) => ({
                                ...previous,
                                dtPrevista:
                                  event.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="col-span-2">
                          <Label>Descrição</Label>
                          <textarea
                            className="input textarea"
                            rows={3}
                            value={formTask.descricao}
                            onChange={(event) =>
                              setFormTask((previous) => ({
                                ...previous,
                                descricao: event.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="col-span-2 flex justify-end">
                          <Button
                            type="submit"
                            disabled={operationalBusy}
                          >
                            {operationalBusy
                              ? "Salvando..."
                              : "Criar tarefa"}
                          </Button>
                        </div>
                      </form>
                    </section>

                    <section className="project-detail-card">
                      <div className="section-title-row">
                        <div>
                          <h3>Tarefas do projeto</h3>
                          <p>
                            Responsabilidade individual dentro de uma
                            entrega colaborativa.
                          </p>
                        </div>
                      </div>

                      {(detail.tarefas || []).length === 0 ? (
                        <div className="empty-sub">
                          Nenhuma tarefa cadastrada.
                        </div>
                      ) : (
                        <div className="project-task-list">
                          {(detail.tarefas || []).map((task) => (
                            <article
                              key={task.ID_TAREFA}
                              className={`project-task-card status-${String(
                                normalizeStatus(task.STATUS) ||
                                  task.STATUS ||
                                  "PENDENTE"
                              ).toLowerCase()}`}
                            >
                              <div className="task-main">
                                <div className="task-title-line">
                                  <span className="mono">
                                    #{task.ID_TAREFA}
                                  </span>
                                  <strong>{task.TITULO}</strong>
                                </div>

                                <p>
                                  {task.DESCRICAO ||
                                    "Sem descrição."}
                                </p>

                                <div className="task-metrics">
                                  <span>
                                    Estimativa:{" "}
                                    {task.ESTIMATIVA_H ?? "-"}h
                                  </span>
                                  <span>
                                    Apontado: {task.GASTO_H ?? 0}h
                                  </span>
                                  <span>
                                    Previsão:{" "}
                                    {task.DT_PREVISTA
                                      ? new Date(
                                          task.DT_PREVISTA
                                        ).toLocaleString()
                                      : "-"}
                                  </span>
                                </div>
                              </div>

                              <div className="task-controls">
                                <div>
                                  <Label>Status</Label>
                                  <select
                                    className="input native"
                                    value={
                                      normalizeStatus(
                                        task.STATUS
                                      ) || "PENDENTE"
                                    }
                                    disabled={operationalBusy}
                                    onChange={(event) =>
                                      atualizarTarefaProjeto(
                                        task.ID_TAREFA,
                                        {
                                          status:
                                            event.target.value,
                                          dtConclusao:
                                            event.target.value ===
                                            STATUS.CONCLUIDO
                                              ? new Date().toISOString()
                                              : null,
                                        }
                                      )
                                    }
                                  >
                                    {TASK_STATUSES.map(
                                      (item) => (
                                        <option
                                          key={item}
                                          value={item}
                                        >
                                          {STATUS_LABEL[item]}
                                        </option>
                                      )
                                    )}
                                  </select>
                                </div>

                                <div>
                                  <Label>Responsável</Label>
                                  <select
                                    className="input native"
                                    value={
                                      task.RESPONSAVEL_ID || ""
                                    }
                                    disabled={operationalBusy}
                                    onChange={(event) =>
                                      atualizarTarefaProjeto(
                                        task.ID_TAREFA,
                                        {
                                          responsavelId:
                                            event.target.value
                                              ? Number(
                                                  event.target
                                                    .value
                                                )
                                              : null,
                                        }
                                      )
                                    }
                                  >
                                    <option value="">
                                      Sem responsável
                                    </option>

                                    {getActiveParticipants(
                                      detail
                                    ).map((participant) => (
                                      <option
                                        key={participant.ID_DEV}
                                        value={
                                          participant.ID_DEV
                                        }
                                      >
                                        {getName(
                                          participant.ID_DEV
                                        )}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </article>
                          ))}
                        </div>
                      )}
                    </section>
                  </div>
                )}

                {detailTab === "bloqueios" && (
                  <div className="project-detail-section">
                    <section className="project-detail-card">
                      <div className="section-title-row">
                        <div>
                          <h3>Registrar bloqueio</h3>
                          <p>
                            Defina motivo, responsável, próxima ação e
                            data de cobrança.
                          </p>
                        </div>
                      </div>

                      <form
                        className="grid grid-2 project-block-form"
                        onSubmit={criarBloqueioProjeto}
                      >
                        <div>
                          <Label>Tipo</Label>
                          <select
                            className="input native"
                            value={formBlock.tipoBloqueio}
                            onChange={(event) =>
                              setFormBlock((previous) => ({
                                ...previous,
                                tipoBloqueio:
                                  event.target.value,
                              }))
                            }
                          >
                            {TIPOS_BLOQUEIO.map((item) => (
                              <option key={item} value={item}>
                                {TIPO_BLOQUEIO_LABEL[item]}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <Label>Tarefa relacionada</Label>
                          <select
                            className="input native"
                            value={formBlock.idTarefa}
                            onChange={(event) =>
                              setFormBlock((previous) => ({
                                ...previous,
                                idTarefa:
                                  event.target.value,
                              }))
                            }
                          >
                            <option value="">
                              Projeto inteiro
                            </option>

                            {(detail.tarefas || []).map((task) => (
                              <option
                                key={task.ID_TAREFA}
                                value={task.ID_TAREFA}
                              >
                                #{task.ID_TAREFA} — {task.TITULO}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-span-2">
                          <Label>Motivo *</Label>
                          <textarea
                            className="input textarea"
                            rows={3}
                            value={formBlock.motivo}
                            onChange={(event) =>
                              setFormBlock((previous) => ({
                                ...previous,
                                motivo: event.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="col-span-2">
                          <Label>Próxima ação</Label>
                          <textarea
                            className="input textarea"
                            rows={2}
                            value={formBlock.proximaAcao}
                            onChange={(event) =>
                              setFormBlock((previous) => ({
                                ...previous,
                                proximaAcao:
                                  event.target.value,
                              }))
                            }
                            placeholder="Ex.: Cobrar retorno do cliente com o suporte."
                          />
                        </div>

                        <div>
                          <Label>Responsável pela resolução</Label>
                          <select
                            className="input native"
                            value={
                              formBlock.responsavelResolucaoId
                            }
                            onChange={(event) =>
                              setFormBlock((previous) => ({
                                ...previous,
                                responsavelResolucaoId:
                                  event.target.value,
                              }))
                            }
                          >
                            <option value="">
                              Não definido
                            </option>

                            {getActiveParticipants(detail).map(
                              (participant) => (
                                <option
                                  key={participant.ID_DEV}
                                  value={participant.ID_DEV}
                                >
                                  {getName(participant)}
                                </option>
                              )
                            )}
                          </select>
                        </div>

                        <div>
                          <Label>Próxima revisão</Label>
                          <Input
                            type="datetime-local"
                            value={formBlock.dtProximaRevisao}
                            onChange={(event) =>
                              setFormBlock((previous) => ({
                                ...previous,
                                dtProximaRevisao:
                                  event.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="col-span-2 flex justify-end">
                          <Button
                            type="submit"
                            disabled={operationalBusy}
                          >
                            Registrar bloqueio
                          </Button>
                        </div>
                      </form>
                    </section>

                    <section className="project-detail-card">
                      <div className="section-title-row">
                        <div>
                          <h3>Histórico de bloqueios</h3>
                          <p>
                            Bloqueios abertos aparecem primeiro.
                          </p>
                        </div>
                      </div>

                      {(detail.bloqueios || []).length === 0 ? (
                        <div className="empty-sub">
                          Nenhum bloqueio registrado.
                        </div>
                      ) : (
                        <div className="project-block-list">
                          {(detail.bloqueios || []).map(
                            (block) => {
                              const isOpen =
                                block.STATUS_BLOQUEIO ===
                                "ABERTO";

                              return (
                                <article
                                  key={block.ID_BLOQUEIO}
                                  className={`project-block-card ${
                                    isOpen
                                      ? "is-open"
                                      : "is-resolved"
                                  }`}
                                >
                                  <div className="block-header">
                                    <div>
                                      <span
                                        className={`block-status ${
                                          isOpen
                                            ? "open"
                                            : "resolved"
                                        }`}
                                      >
                                        {isOpen
                                          ? "Aberto"
                                          : "Resolvido"}
                                      </span>

                                      <strong>
                                        {TIPO_BLOQUEIO_LABEL[
                                          block.TIPO_BLOQUEIO
                                        ] ||
                                          block.TIPO_BLOQUEIO}
                                      </strong>
                                    </div>

                                    <span className="mono">
                                      #{block.ID_BLOQUEIO}
                                    </span>
                                  </div>

                                  <p className="block-reason">
                                    {block.MOTIVO}
                                  </p>

                                  <div className="block-meta-grid">
                                    <span>
                                      <b>Tarefa:</b>{" "}
                                      {block.TAREFA_TITULO ||
                                        "Projeto inteiro"}
                                    </span>

                                    <span>
                                      <b>Responsável:</b>{" "}
                                      {block.RESPONSAVEL_RESOLUCAO_ID
                                        ? getName(
                                            block.RESPONSAVEL_RESOLUCAO_ID
                                          )
                                        : "-"}
                                    </span>

                                    <span>
                                      <b>Próxima revisão:</b>{" "}
                                      {block.DT_PROXIMA_REVISAO
                                        ? new Date(
                                            block.DT_PROXIMA_REVISAO
                                          ).toLocaleString()
                                        : "-"}
                                    </span>

                                    <span>
                                      <b>Próxima ação:</b>{" "}
                                      {block.PROXIMA_ACAO || "-"}
                                    </span>
                                  </div>

                                  {!isOpen && block.SOLUCAO && (
                                    <div className="block-solution">
                                      <b>Solução:</b>{" "}
                                      {block.SOLUCAO}
                                    </div>
                                  )}

                                  {isOpen && (
                                    <div className="block-actions">
                                      {Number(
                                        resolucaoBloqueio.idBloqueio
                                      ) ===
                                      Number(
                                        block.ID_BLOQUEIO
                                      ) ? (
                                        <div className="block-resolution-form">
                                          <textarea
                                            className="input textarea"
                                            rows={3}
                                            placeholder="Descreva a solução aplicada."
                                            value={
                                              resolucaoBloqueio.texto
                                            }
                                            onChange={(event) =>
                                              setResolucaoBloqueio(
                                                {
                                                  idBloqueio:
                                                    block.ID_BLOQUEIO,
                                                  texto:
                                                    event.target
                                                      .value,
                                                }
                                              )
                                            }
                                          />

                                          <div className="flex justify-end">
                                            <Button
                                              variant="secondary"
                                              onClick={() =>
                                                setResolucaoBloqueio(
                                                  {
                                                    idBloqueio:
                                                      null,
                                                    texto: "",
                                                  }
                                                )
                                              }
                                            >
                                              Cancelar
                                            </Button>

                                            <Button
                                              disabled={
                                                operationalBusy ||
                                                !resolucaoBloqueio.texto.trim()
                                              }
                                              onClick={
                                                resolverBloqueioProjeto
                                              }
                                            >
                                              Resolver
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <Button
                                          onClick={() =>
                                            setResolucaoBloqueio(
                                              {
                                                idBloqueio:
                                                  block.ID_BLOQUEIO,
                                                texto: "",
                                              }
                                            )
                                          }
                                        >
                                          Registrar solução
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </article>
                              );
                            }
                          )}
                        </div>
                      )}
                    </section>
                  </div>
                )}

                {detailTab === "tempo" && (
                  <div className="project-detail-section">
                    <div className="time-summary-grid">
                      <article>
                        <span>Sessões registradas</span>
                        <strong>{sessions.length}</strong>
                      </article>

                      <article>
                        <span>Sessões ativas</span>
                        <strong>
                          {(detail.sessoesAtivas || []).length}
                        </strong>
                      </article>

                      <article>
                        <span>Tempo acumulado</span>
                        <strong>
                          {formatDuration(
                            sessions.reduce(
                              (totalSeconds, session) =>
                                totalSeconds +
                                getElapsedSessionSeconds(session),
                              0
                            )
                          )}
                        </strong>
                      </article>
                    </div>

                    <section className="project-detail-card">
                      <div className="section-title-row">
                        <div>
                          <h3>Histórico de trabalho</h3>
                          <p>
                            O tempo é calculado pelas sessões, não apenas
                            pelo status do projeto.
                          </p>
                        </div>

                        <Button
                          variant="outline"
                          onClick={() =>
                            fetchSessions(detail.ID_PROJETO)
                          }
                          disabled={loadingSessions}
                        >
                          {loadingSessions
                            ? "Atualizando..."
                            : "Atualizar tempo"}
                        </Button>
                      </div>

                      {loadingSessions ? (
                        <div>Carregando sessões…</div>
                      ) : sessions.length === 0 ? (
                        <div className="empty-sub">
                          Nenhuma sessão registrada.
                        </div>
                      ) : (
                        <div className="session-history-list">
                          {sessions.map((session) => (
                            <article
                              key={session.ID_SESSAO}
                              className={`session-history-card status-${String(
                                session.STATUS_SESSAO ||
                                  "ENCERRADA"
                              ).toLowerCase()}`}
                            >
                              <div>
                                <strong>
                                  {getName(session)}
                                </strong>
                                <span>
                                  {session.TAREFA_TITULO ||
                                    "Atividade geral do projeto"}
                                </span>
                              </div>

                              <div className="session-period">
                                <span>
                                  {session.DT_INICIO
                                    ? new Date(
                                        session.DT_INICIO
                                      ).toLocaleString()
                                    : "-"}
                                </span>
                                <span>
                                  até{" "}
                                  {session.DT_FIM
                                    ? new Date(
                                        session.DT_FIM
                                      ).toLocaleString()
                                    : "agora"}
                                </span>
                              </div>

                              <div className="session-duration">
                                <strong>
                                  {formatDuration(
                                    getElapsedSessionSeconds(
                                      session
                                    )
                                  )}
                                </strong>
                                <span>
                                  {session.STATUS_SESSAO}
                                </span>
                              </div>
                            </article>
                          ))}
                        </div>
                      )}
                    </section>
                  </div>
                )}

                {detailTab === "comentarios" && (
                  <div className="project-detail-section">
                    <section className="project-detail-card">
                      <div className="section-title-row">
                        <div>
                          <h3>Comentários e apontamentos</h3>
                          <p>
                            Registre decisões e acompanhamentos do projeto.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-4">
                        <div className="col-span-3">
                          <Label>Novo comentário</Label>
                          <textarea
                            className="input textarea"
                            rows={4}
                            placeholder="Escreva seu comentário. Pressione Ctrl+Enter para enviar."
                            value={commentText}
                            onChange={(event) =>
                              setCommentText(event.target.value)
                            }
                            onKeyDown={(event) => {
                              if (
                                event.key === "Enter" &&
                                event.ctrlKey
                              ) {
                                event.preventDefault();
                                postComment();
                              }
                            }}
                          />
                        </div>

                        <div className="col-span-1 flex items-end">
                          <Button
                            onClick={postComment}
                            disabled={
                              postingComment ||
                              !commentText.trim()
                            }
                          >
                            {postingComment
                              ? "Enviando..."
                              : "Enviar"}
                          </Button>
                        </div>
                      </div>

                      <div className="divider" />

                      {loadingComments ? (
                        <div>Carregando comentários…</div>
                      ) : comments.length === 0 ? (
                        <div className="empty-sub">
                          Sem comentários.
                        </div>
                      ) : (
                        <ul className="comments-list">
                          {comments.map((comment) => (
                            <li
                              key={comment.ID_COMENTARIO}
                              className="comment-item"
                            >
                              <div className="comment-header">
                                <b>
                                  {pickName(comment) ||
                                    getName(comment)}
                                </b>

                                <span className="dot">•</span>

                                <span>
                                  {comment.CREATED_AT
                                    ? new Date(
                                        comment.CREATED_AT
                                      ).toLocaleString()
                                    : "-"}
                                </span>

                                {comment.AUTOR_ID &&
                                  Number(comment.AUTOR_ID) ===
                                    Number(MY_ID) &&
                                  !comment._optimistic && (
                                    <Button
                                      variant="ghost"
                                      onClick={() =>
                                        deleteComment(comment)
                                      }
                                      style={{
                                        marginLeft: "auto",
                                      }}
                                    >
                                      Remover
                                    </Button>
                                  )}
                              </div>

                              <div className="comment-body">
                                {comment.TEXTO}
                              </div>

                              {comment._optimistic && (
                                <small className="muted">
                                  enviando…
                                </small>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  </div>
                )}

                {detailTab === "anexos" && (
                  <div className="project-detail-section">
                    <section className="project-detail-card">
                      <div className="section-title-row">
                        <div>
                          <h3>Anexos</h3>
                          <p>
                            Documentos, imagens e evidências do projeto.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-4">
                        <div className="col-span-3">
                          <Label>Selecionar arquivo</Label>
                          <input
                            className="input native"
                            type="file"
                            onChange={(event) =>
                              setFileToUpload(
                                event.target.files?.[0] ||
                                  null
                              )
                            }
                          />
                        </div>

                        <div className="col-span-1 flex items-end">
                          <Button
                            onClick={uploadAttachment}
                            disabled={
                              !fileToUpload ||
                              uploadingAttachment
                            }
                          >
                            {uploadingAttachment
                              ? "Enviando..."
                              : "Upload"}
                          </Button>
                        </div>
                      </div>

                      <div className="divider" />

                      {loadingAttachments ? (
                        <div>Carregando anexos…</div>
                      ) : attachments.length === 0 ? (
                        <div className="empty-sub">
                          Sem anexos.
                        </div>
                      ) : (
                        <ul className="attachments-list">
                          {attachments.map((attachment) => (
                            <li
                              key={attachment.id}
                              className="file-row"
                            >
                              <a
                                href={attachment.url}
                                target="_blank"
                                rel="noreferrer"
                                className="file-name"
                                title={attachment.name}
                              >
                                {attachment.name}
                              </a>

                              {attachment.sizeHuman && (
                                <span className="file-meta">
                                  {attachment.sizeHuman}
                                </span>
                              )}

                              <div className="file-actions">
                                <Button
                                  variant="ghost"
                                  onClick={() =>
                                    downloadAttachmentProjetos(
                                      detail,
                                      attachment
                                    )
                                  }
                                  disabled={
                                    attachment.__optimistic
                                  }
                                >
                                  Baixar
                                </Button>

                                <Button
                                  variant="ghost"
                                  onClick={() =>
                                    deleteAttachment(
                                      attachment.id
                                    )
                                  }
                                >
                                  Remover
                                </Button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Drawer: Criar */}
        <PanelModal
          open={createOpen}
          title="Novo Projeto"
          size="lg"
          onClose={() => setCreateOpen(false)}
        >
          {({ firstFieldRef }) => (
            <>
              <form onSubmit={submitCreate} className="grid grid-2">
                <div>
                  <Label>Título*</Label>
                  <Input
                    ref={firstFieldRef}
                    value={formCreate.titulo}
                    onChange={(e) =>
                      setFormCreate((p) => ({ ...p, titulo: e.target.value }))
                    }
                    aria-invalid={!!formCreateErrors.titulo}
                  />
                  {formCreateErrors.titulo && (
                    <small style={{ color: "#b91c1c" }}>
                      {formCreateErrors.titulo}
                    </small>
                  )}
                </div>

                <div>
                  <Label>Prioridade*</Label>
                  <select
                    className="input native"
                    value={formCreate.prioridade}
                    onChange={(e) =>
                      setFormCreate((p) => ({
                        ...p,
                        prioridade: e.target.value,
                      }))
                    }
                    aria-invalid={!!formCreateErrors.prioridade}
                  >
                    {PRIORIDADES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  {formCreateErrors.prioridade && (
                    <small style={{ color: "#b91c1c" }}>
                      {formCreateErrors.prioridade}
                    </small>
                  )}
                </div>

                <div>
                  <Label>Tipo do projeto</Label>
                  <select
                    className="input native"
                    value={formCreate.tipoProjeto}
                    onChange={(event) =>
                      setFormCreate((previous) => ({
                        ...previous,
                        tipoProjeto: event.target.value,
                      }))
                    }
                  >
                    {TIPOS_PROJETO.map((item) => (
                      <option key={item} value={item}>
                        {TIPO_PROJETO_LABEL[item]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="project-checkbox-field">
                  <Label>Cadastro</Label>
                  <label className="project-check">
                    <input
                      type="checkbox"
                      checked={!!formCreate.cadastroProvisorio}
                      onChange={(event) =>
                        setFormCreate((previous) => ({
                          ...previous,
                          cadastroProvisorio:
                            event.target.checked,
                        }))
                      }
                    />
                    <span>
                      Criar como provisório para formalizar depois
                    </span>
                  </label>
                </div>

                <div className="col-span-2">
                  <Label>Descrição</Label>
                  <textarea
                    className="input textarea"
                    rows={4}
                    value={formCreate.descricao}
                    onChange={(e) =>
                      setFormCreate((p) => ({
                        ...p,
                        descricao: e.target.value,
                      }))
                    }
                    placeholder="conte o que precisa ser feito…"
                  />
                </div>

                <div>
                  <Label>Início</Label>
                  <Input
                    type="datetime-local"
                    value={formCreate.dtInicio}
                    onChange={(e) =>
                      setFormCreate((p) => ({ ...p, dtInicio: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <Label>Prevista</Label>
                  <Input
                    type="datetime-local"
                    value={formCreate.dtPrevista}
                    onChange={(e) =>
                      setFormCreate((p) => ({
                        ...p,
                        dtPrevista: e.target.value,
                      }))
                    }
                  />
                </div>

                {/* Atribuição inicial opcional */}
                <div className="col-span-2">
                  <Label>
                    Responsável principal inicial (opcional) — Setor
                  </Label>
                  <select
                    className="input native"
                    value={createCargo}
                    onChange={(e) => {
                      setCreateCargo(e.target.value);
                      setCreateDevId("");
                      if (e.target.value)
                        fetchFuncsByCargo(e.target.value, "create");
                    }}
                  >
                    <option value="">
                      {loadingCargos ? "Carregando..." : "(não atribuir)"}
                    </option>
                    {cargos.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <Label>Funcionário</Label>
                  <select
                    className="input native"
                    value={createDevId}
                    onChange={(e) => setCreateDevId(e.target.value)}
                    disabled={!createCargo || loadingCreateFuncs}
                  >
                    {!createCargo && (
                      <option value="">(selecione primeiro o setor)</option>
                    )}
                    {createCargo && loadingCreateFuncs && (
                      <option value="">Carregando...</option>
                    )}
                    {createCargo &&
                      !loadingCreateFuncs &&
                      createFuncs.map((opt) => (
                        <option key={opt.id} value={String(opt.id)}>
                          {opt.label}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Anexos iniciais (imagens) */}
                <div className="col-span-2">
                  <Label>Anexos (imagens) — opcional</Label>
                  <input
                    className="input native"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) =>
                      setCreateFiles(Array.from(e.target.files || []))
                    }
                  />
                  {createFiles.length > 0 && (
                    <div
                      className="thumbs"
                      style={{
                        display: "flex",
                        gap: 8,
                        marginTop: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      {createFiles.map((f, i) => (
                        <img
                          key={i}
                          src={URL.createObjectURL(f)}
                          alt={f.name}
                          className="thumb"
                          style={{
                            width: 64,
                            height: 64,
                            objectFit: "cover",
                            borderRadius: 6,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div
                  className="flex justify-end mt-3 col-span-2"
                  style={{ gap: 8 }}
                >
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setCreateOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={creating}>
                    {creating ? "Gravando..." : "Criar"}
                  </Button>
                </div>
              </form>

              {DEBUG && (
                <details style={{ marginTop: 12 }}>
                  <summary>DEBUG (create)</summary>
                  <pre style={{ whiteSpace: "pre-wrap" }}>
                    {JSON.stringify(createDebug, null, 2)}
                  </pre>
                </details>
              )}
            </>
          )}
        </PanelModal>

        {/* Drawer: Editar */}
        <PanelModal
          open={editOpen}
          title={`Editar Projeto #${editId || ""}`}
          size="xl"
          onClose={() => setEditOpen(false)}
        >
          {({ firstFieldRef }) => (
            <>
              <div className="tabs" style={{ marginBottom: 12 }}>
                <button
                  className={`tab ${editTab === "form" ? "active" : ""}`}
                  onClick={() => setEditTab("form")}
                >
                  Atualizar
                </button>
                <button
                  className={`tab ${editTab === "comentarios" ? "active" : ""}`}
                  onClick={() => setEditTab("comentarios")}
                >
                  Comentários
                </button>
                <button
                  className={`tab ${editTab === "anexos" ? "active" : ""}`}
                  onClick={() => setEditTab("anexos")}
                >
                  Anexos {attachments.length ? `(${attachments.length})` : ""}
                </button>
              </div>

              {editTab === "form" && (
                <form onSubmit={submitEdit} className="grid grid-2">
                  <div className="col-span-2">
                    <Label>Título*</Label>
                    <Input
                      ref={firstFieldRef}
                      value={formEdit.titulo}
                      onChange={(e) =>
                        setFormEdit((p) => ({ ...p, titulo: e.target.value }))
                      }
                      aria-invalid={!!formEditErrors.titulo}
                    />
                    {formEditErrors.titulo && (
                      <small style={{ color: "#b91c1c" }}>
                        {formEditErrors.titulo}
                      </small>
                    )}
                  </div>
                  <div>
                    <Label>Prioridade*</Label>
                    <select
                      className="input native"
                      value={formEdit.prioridade}
                      onChange={(e) =>
                        setFormEdit((p) => ({
                          ...p,
                          prioridade: e.target.value,
                        }))
                      }
                      aria-invalid={!!formEditErrors.prioridade}
                    >
                      {PRIORIDADES.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                    {formEditErrors.prioridade && (
                      <small style={{ color: "#b91c1c" }}>
                        {formEditErrors.prioridade}
                      </small>
                    )}
                  </div>

                  <div>
                    <Label>Tipo do projeto</Label>
                    <select
                      className="input native"
                      value={formEdit.tipoProjeto}
                      onChange={(event) =>
                        setFormEdit((previous) => ({
                          ...previous,
                          tipoProjeto: event.target.value,
                        }))
                      }
                    >
                      {TIPOS_PROJETO.map((item) => (
                        <option key={item} value={item}>
                          {TIPO_PROJETO_LABEL[item]}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label>ID do gestor</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formEdit.gestorProjetoId}
                      onChange={(event) =>
                        setFormEdit((previous) => ({
                          ...previous,
                          gestorProjetoId: event.target.value,
                        }))
                      }
                      placeholder="Deixe vazio para não definir"
                    />
                  </div>

                  <div className="project-checkbox-field">
                    <Label>Cadastro</Label>
                    <label className="project-check">
                      <input
                        type="checkbox"
                        checked={!!formEdit.cadastroProvisorio}
                        onChange={(event) =>
                          setFormEdit((previous) => ({
                            ...previous,
                            cadastroProvisorio:
                              event.target.checked,
                          }))
                        }
                      />
                      <span>Cadastro provisório</span>
                    </label>
                  </div>

                  <div>
                    <Label>Início</Label>
                    <Input
                      type="datetime-local"
                      value={formEdit.dtInicio}
                      onChange={(e) =>
                        setFormEdit((p) => ({ ...p, dtInicio: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <Label>Prevista</Label>
                    <Input
                      type="datetime-local"
                      value={formEdit.dtPrevista}
                      onChange={(e) =>
                        setFormEdit((p) => ({
                          ...p,
                          dtPrevista: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Descrição</Label>
                    <textarea
                      className="input textarea"
                      rows={5}
                      value={formEdit.descricao}
                      onChange={(e) =>
                        setFormEdit((p) => ({
                          ...p,
                          descricao: e.target.value,
                        }))
                      }
                      placeholder="detalhe o escopo, critérios, etc."
                    />
                  </div>
                  <div
                    className="flex justify-end mt-3 col-span-2"
                    style={{ gap: 8 }}
                  >
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setEditOpen(false)}
                    >
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={editing}>
                      {editing ? "Gravando..." : "Salvar alterações"}
                    </Button>
                  </div>
                </form>
              )}

              {editTab === "comentarios" && (
                <div>
                  <h3 style={{ marginTop: 0 }}>Comentários</h3>
                  <div className="grid grid-4">
                    <div className="col-span-3">
                      <Label>Novo comentário</Label>
                      <textarea
                        className="input textarea"
                        rows={4}
                        placeholder="Escreva seu comentário. Pressione CtrlEnter para enviar."
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.ctrlKey) {
                            e.preventDefault();
                            postComment();
                          }
                        }}
                      />
                    </div>
                    <div className="col-span-1 flex items-end">
                      <Button
                        onClick={postComment}
                        disabled={postingComment || !commentText.trim()}
                      >
                        {postingComment ? "Enviando..." : "Enviar (CtrlEnter)"}
                      </Button>
                    </div>
                  </div>
                  <div className="divider" />
                  {loadingComments ? (
                    <div>Carregando comentários…</div>
                  ) : comments.length === 0 ? (
                    <div className="empty-sub">Sem comentários.</div>
                  ) : (
                    <ul className="comments-list">
                      {comments.map((c) => (
                        <li key={c.ID_COMENTARIO} className="comment-item">
                          <div className="comment-header">
                            <b>{pickName(c) || getName(c)}</b>
                            <span className="dot">•</span>
                            <span>
                              {c.CREATED_AT
                                ? new Date(c.CREATED_AT).toLocaleString()
                                : "-"}
                            </span>
                            {c.AUTOR_ID &&
                              Number(c.AUTOR_ID) === Number(MY_ID) &&
                              !c._optimistic && (
                                <Button
                                  variant="ghost"
                                  onClick={() => deleteComment(c)}
                                  style={{ marginLeft: "auto" }}
                                  title="Remover comentário"
                                >
                                  Remover
                                </Button>
                              )}
                          </div>
                          <div className="comment-body">{c.TEXTO}</div>
                          {c._optimistic && (
                            <small className="muted">enviando…</small>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {editTab === "anexos" && (
                <>
                  <h3 style={{ marginTop: 0 }}>Anexos</h3>
                  <div className="grid grid-4">
                    <div className="col-span-3">
                      <Label>Selecionar arquivo</Label>
                      <input
                        className="input native"
                        type="file"
                        onChange={(e) =>
                          setFileToUpload(e.target.files?.[0] || null)
                        }
                      />
                    </div>
                    <div className="col-span-1 flex items-end">
                      <Button
                        onClick={uploadAttachment}
                        disabled={!fileToUpload || uploadingAttachment}
                      >
                        {uploadingAttachment ? "Enviando..." : "Upload"}
                      </Button>
                    </div>
                  </div>
                  <div className="divider" />
                  {loadingAttachments ? (
                    <div>Carregando anexos…</div>
                  ) : attachments.length === 0 ? (
                    <div className="empty-sub">Sem anexos.</div>
                  ) : (
                    <ul className="attachments-list">
                      {attachments.map((a) => (
                        <li key={a.id} className="file-row">
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="file-name"
                            title={a.name}
                          >
                            {a.name}
                          </a>
                          {a.sizeHuman && (
                            <span className="file-meta">{a.sizeHuman}</span>
                          )}
                          <div className="file-actions">
                            <Button
                              variant="ghost"
                              onClick={() =>
                                downloadAttachmentProjetos(
                                  { ID_PROJETO: editId },
                                  a
                                )
                              }
                              title="Baixar"
                              disabled={a.__optimistic}
                            >
                              Baixar
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => deleteAttachment(a.id)}
                              title="Remover anexo"
                            >
                              Remover
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
              {DEBUG && (
                <details style={{ marginTop: 12 }}>
                  <summary>DEBUG (edit)</summary>
                  <pre style={{ whiteSpace: "pre-wrap" }}>
                    {JSON.stringify(editDebug, null, 2)}
                  </pre>
                </details>
              )}
            </>
          )}
        </PanelModal>

        {/* Drawer: adicionar participante / substituir equipe */}
        <PanelModal
          open={assignOpen}
          title="Adicionar participante"
          size="md"
          onClose={() => setAssignOpen(false)}
        >
          {({ firstFieldRef }) => {
            const principalAtual = assignFor
              ? getName(assignedToMap[assignFor])
              : "-";

            const projetoAtribuicao =
              rows.find(
                (item) =>
                  Number(item.ID_PROJETO) ===
                  Number(assignFor)
              ) ||
              (Number(detail?.ID_PROJETO) === Number(assignFor)
                ? detail
                : null);

            const statusAtribuicaoOptions =
              getStatusOptionsForProject(
                projetoAtribuicao,
                assignStatus
              );

            const activeParticipantIds = new Set(
              Number(detail?.ID_PROJETO) === Number(assignFor)
                ? getActiveParticipants(detail).map((item) =>
                    Number(item.ID_DEV)
                  )
                : []
            );

            const availableParticipantOptions = funcOptions.filter(
              (option) =>
                assignExclusive ||
                !activeParticipantIds.has(Number(option.id))
            );

            return (
              <>
                <div className="assignment-context-card">
                  <span>Projeto</span>
                  <strong>
                    #{assignFor} —{" "}
                    {projetoAtribuicao?.TITULO ||
                      detail?.TITULO ||
                      "Projeto"}
                  </strong>
                  <small>
                    Responsável principal atual:{" "}
                    {principalAtual || "-"}
                  </small>
                </div>

                <form
                  onSubmit={submitAssign}
                  className="grid grid-2 assignment-form"
                >
                  <div className="col-span-2">
                    <Label>Setor/Cargo</Label>
                    <select
                      ref={firstFieldRef}
                      className="input native"
                      value={selectedCargo}
                      onChange={(event) => {
                        setSelectedCargo(event.target.value);
                        setAssignDevId("");
                      }}
                    >
                      <option value="">
                        {loadingCargos
                          ? "Carregando..."
                          : "(selecione)"}
                      </option>

                      {cargos.map((cargo) => (
                        <option key={cargo} value={cargo}>
                          {cargo}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-2">
                    <Label>Participante</Label>
                    <select
                      className="input native"
                      value={assignDevId}
                      onChange={(event) =>
                        setAssignDevId(
                          String(event.target.value)
                        )
                      }
                      disabled={
                        !selectedCargo || loadingFuncs
                      }
                    >
                      <option value="">
                        {!selectedCargo
                          ? "Selecione primeiro o setor"
                          : loadingFuncs
                          ? "Carregando técnicos..."
                          : "Selecione o Técnico"}
                      </option>

                      {selectedCargo &&
                        !loadingFuncs &&
                        availableParticipantOptions.length === 0 && (
                          <option value="" disabled>
                            Todos os técnicos deste setor já participam
                          </option>
                        )}

                      {availableParticipantOptions.map((option) => (
                        <option
                          key={option.id}
                          value={String(option.id)}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label>Papel</Label>
                    <select
                      className="input native"
                      value={
                        assignPrincipal
                          ? "RESPONSAVEL_PRINCIPAL"
                          : assignRole
                      }
                      disabled={assignPrincipal}
                      onChange={(event) =>
                        setAssignRole(event.target.value)
                      }
                    >
                      {PAPEIS_PROJETO.filter(
                        (item) =>
                          item !== "RESPONSAVEL_PRINCIPAL"
                      ).map((item) => (
                        <option key={item} value={item}>
                          {PAPEL_PROJETO_LABEL[item]}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label>Status após inclusão (opcional)</Label>
                    <select
                      className="input native"
                      value={assignStatus}
                      onChange={(event) =>
                        setAssignStatus(event.target.value)
                      }
                    >
                      <option value="">
                        Manter status atual
                      </option>

                      {statusAtribuicaoOptions.map((item) => (
                        <option
                          key={item}
                          value={item}
                          disabled={
                            isProjetoOriginadoDeChamado(
                              projetoAtribuicao
                            ) &&
                            STATUS_BLOQUEADOS_PROJETO_CHAMADO.has(
                              item
                            )
                          }
                        >
                          {STATUS_LABEL[item]}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-2 assignment-options">
                    <label className="project-check">
                      <input
                        type="checkbox"
                        checked={assignPrincipal}
                        onChange={(event) =>
                          setAssignPrincipal(
                            event.target.checked
                          )
                        }
                      />
                      <span>
                        Definir como responsável principal
                      </span>
                    </label>

                    <label className="project-check">
                      <input
                        type="checkbox"
                        checked={assignPodeMovimentar}
                        onChange={(event) =>
                          setAssignPodeMovimentar(
                            event.target.checked
                          )
                        }
                      />
                      <span>
                        Pode alterar status, foco e movimentações
                      </span>
                    </label>

                    <label className="project-check danger-option">
                      <input
                        type="checkbox"
                        checked={assignExclusive}
                        onChange={(event) =>
                          setAssignExclusive(
                            event.target.checked
                          )
                        }
                      />
                      <span>
                        Substituir toda a equipe atual
                      </span>
                    </label>
                  </div>

                  {assignExclusive && (
                    <div className="col-span-2 assignment-warning">
                      A opção de substituição desativará os demais
                      participantes. Use somente em transferência
                      completa de responsabilidade.
                    </div>
                  )}

                  {isProjetoOriginadoDeChamado(
                    projetoAtribuicao
                  ) && (
                    <div className="col-span-2 project-status-lock-note">
                      Este projeto veio do chamado #
                      {projetoAtribuicao.ID_CHAMADO_ORIGEM}.
                      Finalização e conclusão direta permanecem
                      bloqueadas.
                    </div>
                  )}

                  <div className="col-span-2">
                    <Label>Justificativa / orientação</Label>
                    <textarea
                      className="input textarea"
                      rows={3}
                      placeholder="Ex.: Backend responsável pela API; frontend inicia após contrato definido."
                      value={assignJust}
                      onChange={(event) =>
                        setAssignJust(event.target.value)
                      }
                    />
                  </div>

                  <div className="flex justify-end mt-3 col-span-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setAssignOpen(false)}
                    >
                      Cancelar
                    </Button>

                    <Button
                      type="submit"
                      disabled={
                        !assignDevId ||
                        loadingFuncs ||
                        applying
                      }
                    >
                      {applying
                        ? "Aplicando..."
                        : assignExclusive
                        ? "Substituir equipe"
                        : "Adicionar participante"}
                    </Button>
                  </div>
                </form>
              </>
            );
          }}
        </PanelModal>

        {/* NOVO Drawer: Gerenciar Fila do Técnico */}
        <PanelModal
          open={manageOpen}
          title={`Gerenciar fila — ${manageDevName}`}
          size="lg"
          onClose={() => setManageOpen(false)}
        >
          {() => (
            <>
              <div className="card soft">
                <div
                  className="flex items-center"
                  style={{ justifyContent: "space-between", marginBottom: 8 }}
                >
                  <div>
                    <b>Técnico:</b> {manageDevName}{" "}
                    <span className="muted">#{manageDevId}</span>
                  </div>
                  <div className="hint">
                    {manageQueueLoading
                      ? "Carregando fila..."
                      : manageQueueDirty
                      ? "Alterações não salvas"
                      : ""}
                  </div>
                </div>

                {manageQueueLoading ? (
                  <div>Carregando…</div>
                ) : manageQueueItems.length === 0 ? (
                  <div className="muted">Sem itens na fila.</div>
                ) : (
                  <div className="queue-list">
                    {manageQueueItems.map((it, idx) => (
                      <div key={it.ID_PROJETO} className="queue-row">
                        <div className="queue-idx">{idx + 1}</div>
                        <div className="queue-main">
                          <div className="queue-title">
                            <span className="mono">#{it.ID_PROJETO}</span>&nbsp;
                            {it.TITULO}
                          </div>
                          <div className="queue-meta">
                            {it.PRIORIDADE ? (
                              <PriorityBadge value={it.PRIORIDADE} />
                            ) : (
                              <span className="muted">-</span>
                            )}

                            <span className="muted">
                              Prevista:{" "}
                              {it.DT_PREVISTA
                                ? new Date(it.DT_PREVISTA).toLocaleDateString()
                                : "-"}
                            </span>
                            {it.SCORE != null && (
                              <span className="muted">Score: {it.SCORE}</span>
                            )}
                            {it.QT_COMENTARIOS != null && (
                              <span className="muted">
                                Com: {it.QT_COMENTARIOS}
                              </span>
                            )}
                            {it.QT_ANEXOS != null && (
                              <span className="muted">Anx: {it.QT_ANEXOS}</span>
                            )}
                          </div>
                        </div>
                        <div className="queue-focus">
                          <label className="radio">
                            <input
                              type="radio"
                              name="manageFocus"
                              checked={manageQueueFocusId === it.ID_PROJETO}
                              onChange={() =>
                                setManageQueueFocusId(it.ID_PROJETO)
                              }
                            />
                            <span>Foco</span>
                          </label>
                        </div>
                        <div className="queue-actions">
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => moveManageItem(idx, "up")}
                            disabled={idx === 0}
                          >
                            ↑
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => moveManageItem(idx, "down")}
                            disabled={idx === manageQueueItems.length - 1}
                          >
                            ↓
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="muted" style={{ marginTop: 8 }}>
                  Dica: use ↑/↓ para priorizar. Apenas um projeto pode estar em
                  FOCO por técnico.
                </div>
              </div>

              <div className="flex justify-end mt-3" style={{ gap: 8 }}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setManageOpen(false)}
                >
                  Fechar
                </Button>
                <Button
                  type="button"
                  onClick={applyManageQueue}
                  disabled={manageQueueLoading || manageApplying}
                >
                  {manageApplying ? "Salvando..." : "Salvar fila e foco"}
                </Button>
              </div>
            </>
          )}
        </PanelModal>
      </div>
    </MainLayout>
  );
}
